import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";

// Status color mapping
const getStatusColor = (status) => {
  const colors = {
    'Completed': '#10B981',
    'InProgress': '#3B82F6',
    'Pending': '#FBBF24',
    'Delayed': '#EF4444',
    'Unallocated': '#E5E7EB'
  };
  return colors[status] || '#9CA3AF';
};

const STATUS_LEGEND = [
  { status: 'Completed', color: '#10B981' },
  { status: 'InProgress', color: '#3B82F6' },
  { status: 'Pending', color: '#FBBF24' },
  { status: 'Delayed', color: '#EF4444' },
  { status: 'Unallocated', color: '#E5E7EB' },
];

const DIMMED_DOT_COLOR = '#D1D5DB';


// One place that decides a dot's colour, so the map and the popup can never
// disagree: a hall with no tracker row for the current service is drawn grey
// rather than given a status it does not have.
const paintBoothDot = (circle, status, dimmed) => {
  circle.setAttribute(
    'fill',
    dimmed ? DIMMED_DOT_COLOR : status ? getStatusColor(status) : '#4f46e5'
  );
};

// Border-wise stall highlighting with properly visible fill opacity
const paintBoothArea = (rect, status, dimmed) => {
  const color = dimmed ? DIMMED_DOT_COLOR : status ? getStatusColor(status) : '#4f46e5';
  rect.setAttribute('fill', color);
  rect.setAttribute('fill-opacity', dimmed ? '0.08' : '0.22');
  rect.setAttribute('stroke', color);
  rect.setAttribute('stroke-opacity', dimmed ? 0.35 : 0.95);
  rect.setAttribute('stroke-width', '1.5');
  rect.setAttribute('stroke-linejoin', 'round');
  rect.setAttribute('vector-effect', 'non-scaling-stroke');
};

// ─── Memoized SVG Component ─────────────────────────────────────────
// This prevents React from re-parsing the massive HTML string when SvgCanvas re-renders
const MemoizedSvg = React.memo(React.forwardRef(({ svgContent }, ref) => (
  <div
    ref={ref}
    style={{
      transformOrigin: "0 0",
      width: "100%",
      height: "100%",
    }}
    dangerouslySetInnerHTML={{ __html: svgContent }}
  />
)), (prev, next) => prev.svgContent === next.svgContent);

const normalizeId = (id) => String(id ?? "").trim().toLowerCase();

// sp_get_hallviewauditTracker selects two columns with the same name (the
// exhibitor's and the vendor's), and the driver collapses each pair into an
// array: CompanyName: ["Acme Looms", "Grade Electricals"], MobileNo likewise.
// Index 0 is the exhibitor, index 1 the vendor. Scalars come back from the
// first recordset, where only the vendor column is selected.
const firstValue = (v) => (Array.isArray(v) ? v.find((x) => x != null && x !== "") : v);

// sp_get_hallviewauditTracker now names the exhibitor's and the vendor's columns
// apart, so nothing collapses into an array. The array unwrapping below is kept
// so the screen still reads a server that has not been updated yet.
const exhibitorField = (row, named, legacy) =>
  row[named] != null ? row[named] : firstValue(row[legacy]);
const vendorField = (row, named, legacy) =>
  row[named] != null ? row[named] : lastValue(row[legacy]);
const lastValue = (v) => {
  if (!Array.isArray(v)) return v;
  for (let i = v.length - 1; i >= 0; i--) {
    if (v[i] != null && v[i] !== "") return v[i];
  }
  return undefined;
};




// Join key for booth/stall codes across the drawing and the database. The
// drawing writes "H1-A1"; the database writes "H1A1". Punctuation is the only
// difference, so strip everything that isn't alphanumeric.
const normalizeBoothKey = (name) =>
  String(name ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

const buildSvgIndex = (svgElement) => {
  const elementsByClip = new Map();
  const clipPathDefsById = new Map();
  const elementsByBoothName = new Map();
  const textElementsByContent = new Map();

  try {
    svgElement.querySelectorAll("clipPath[id]").forEach((cp) => {
      const id = normalizeId(cp.getAttribute("id"));
      if (id) clipPathDefsById.set(id, cp);
    });
  } catch (e) { }

  try {
    svgElement.querySelectorAll("[data-booth-name]").forEach((el) => {
      const name = normalizeId(el.getAttribute("data-booth-name"));
      if (!name) return;
      if (!elementsByBoothName.has(name)) elementsByBoothName.set(name, []);
      elementsByBoothName.get(name).push(el);
    });
  } catch (e) { }

  try {
    svgElement.querySelectorAll("text, tspan").forEach((el) => {
      const txt = normalizeId(el.textContent);
      if (!txt) return;
      if (!textElementsByContent.has(txt)) textElementsByContent.set(txt, []);
      textElementsByContent.get(txt).push(el);
    });
  } catch (e) { }

  const refRegex = /url\(\s*["']?#([^"')]+)["']?\s*\)/i;
  try {
    const candidates = svgElement.querySelectorAll('[clip-path], [style*="clip-path"], [style*="clip-Path"]');
    candidates.forEach((el) => {
      try {
        let raw = el.getAttribute("clip-path");
        if (!raw) {
          const styleAttr = el.getAttribute("style") || "";
          const m = styleAttr.match(/clip-path\s*:\s*([^;]+)/i);
          raw = m ? m[1] : null;
        }
        if (!raw) return;
        const match = raw.match(refRegex);
        if (!match) return;
        const id = normalizeId(match[1]);
        if (!id) return;
        if (!elementsByClip.has(id)) elementsByClip.set(id, []);
        elementsByClip.get(id).push(el);
      } catch (e) { }
    });
  } catch (e) { }

  return { elementsByClip, clipPathDefsById, elementsByBoothName, textElementsByContent };
};

// ─── Layout extraction from the drawing itself ──────────────────────────
// The layout is a PDF export: booth codes live in <text> nodes that carry
// fill-opacity="0" and sit invisibly on top of the vector glyphs. They are the
// only trustworthy booth anchors in the file - the API's ClipId values are
// sequential placeholders that mostly do not exist in the SVG, and the handful
// that do are full-canvas layer clips.
const BOOTH_LABEL_RE = /^H(\d+)\s*-\s*([A-Za-z0-9]+)$/i;
const HALL_TITLE_RE = /^HALLS?\s+(\d+)$/i;
// Groups we inject ourselves - never re-read them as layout content.
const OVERLAY_IDS = "#booth-focus, #booth-nodes, #booth-hits, #route-path";

const cleanText = (el) => (el.textContent || "").replace(/\s+/g, " ").trim();

// "H1-A10" must sort after "H1-A9", not between "H1-A1" and "H1-A2".
const naturalCompare = (a, b) => {
  const ax = String(a).match(/(\d+|\D+)/g) || [];
  const bx = String(b).match(/(\d+|\D+)/g) || [];
  for (let i = 0; i < Math.max(ax.length, bx.length); i++) {
    const av = ax[i];
    const bv = bx[i];
    if (av === undefined) return -1;
    if (bv === undefined) return 1;
    if (/^\d/.test(av) && /^\d/.test(bv)) {
      const diff = Number(av) - Number(bv);
      if (diff !== 0) return diff;
    } else if (av !== bv) {
      return av < bv ? -1 : 1;
    }
  }
  return 0;
};

const isOverlayNode = (el) => {
  try {
    return !!(el.closest && el.closest(OVERLAY_IDS));
  } catch (e) {
    return false;
  }
};

const extractSvgLayoutData = (svgElement) => {
  const elementsByBooth = new Map();
  const hallTitles = new Map();
  const boothsByName = new Map();

  let textNodes = [];
  try {
    textNodes = Array.from(svgElement.querySelectorAll("text"));
  } catch (e) {
    return { halls: [], booths: [], elementsByBooth };
  }

  textNodes.forEach((el) => {
    if (isOverlayNode(el)) return;
    const m = cleanText(el).match(HALL_TITLE_RE);
    if (m) hallTitles.set(m[1], `Hall ${m[1]}`);
  });

  textNodes.forEach((el) => {
    if (isOverlayNode(el)) return;
    const raw = cleanText(el);
    const m = raw.match(BOOTH_LABEL_RE);
    if (!m) return;

    const hallNo = m[1];
    const name = raw.toUpperCase().replace(/\s*-\s*/, "-");
    const hallId = `H${hallNo}`;

    if (!elementsByBooth.has(name)) elementsByBooth.set(name, []);
    elementsByBooth.get(name).push(el);

    if (!boothsByName.has(name)) {
      boothsByName.set(name, {
        BoothID: name,
        BoothName: name,
        HallID: hallId,
        HallName: hallTitles.get(hallNo) || `Hall ${hallNo}`,
        Block: (m[2].match(/^[A-Za-z]+/) || [""])[0].toUpperCase(),
      });
    }

    // Let the existing hover/click delegation find these. The nodes are
    // unpainted, so they also need pointer-events turned on (done in CSS).
    el.setAttribute("data-booth-name", name);
    el.setAttribute("data-hall-id", hallId);
  });

  const booths = Array.from(boothsByName.values()).sort((a, b) =>
    naturalCompare(a.BoothName, b.BoothName)
  );

  const hallMap = new Map();
  booths.forEach((b) => {
    if (!hallMap.has(b.HallID)) {
      hallMap.set(b.HallID, { HallID: b.HallID, HallName: b.HallName, BoothCount: 0 });
    }
    hallMap.get(b.HallID).BoothCount++;
  });
  const halls = Array.from(hallMap.values()).sort((a, b) =>
    naturalCompare(a.HallID, b.HallID)
  );

  return { halls, booths, elementsByBooth };
};

// Matrix from an element's own user space into the <svg> root's USER space,
// i.e. viewBox units - the space our overlays and the nav grid are expressed in.
//
// getCTM() is deliberately not used: it maps to the nearest *viewport*, which
// includes the viewBox->viewport transform. That is identity only while the svg
// renders at its intrinsic size; once it scales to its container the results
// come back in CSS pixels and every overlay lands in the wrong place. Composing
// root.getScreenCTM()^-1 with el.getScreenCTM() cancels the viewport transform
// (and any ancestor CSS transform) and leaves pure user units.
const matrixToSvgUserSpace = (el) => {
  try {
    const root = el.ownerSVGElement;
    if (root && typeof root.getScreenCTM === "function" && typeof el.getScreenCTM === "function") {
      const rootCTM = root.getScreenCTM();
      const elCTM = el.getScreenCTM();
      if (rootCTM && elCTM) return rootCTM.inverse().multiply(elCTM);
    }
  } catch (e) { }
  try {
    return typeof el.getCTM === "function" ? el.getCTM() : null;
  } catch (e) {
    return null;
  }
};

// getBBox() reports the element's own user space; map it into root user space.
const elementBoxInSvgSpace = (el) => {
  try {
    const bbox = el.getBBox();
    if (!(bbox.width > 0 || bbox.height > 0)) return null;
    const ctm = matrixToSvgUserSpace(el);
    if (!ctm) {
      return { x: bbox.x, y: bbox.y, width: bbox.width, height: bbox.height };
    }
    const pts = [
      [bbox.x, bbox.y],
      [bbox.x + bbox.width, bbox.y],
      [bbox.x, bbox.y + bbox.height],
      [bbox.x + bbox.width, bbox.y + bbox.height],
    ].map(([x, y]) => ({
      x: ctm.a * x + ctm.c * y + ctm.e,
      y: ctm.b * x + ctm.d * y + ctm.f,
    }));
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
  } catch (e) {
    return null;
  }
};

const unionBoxes = (boxes) => {
  const valid = boxes.filter(Boolean);
  if (valid.length === 0) return null;
  const minX = Math.min(...valid.map((b) => b.x));
  const minY = Math.min(...valid.map((b) => b.y));
  const maxX = Math.max(...valid.map((b) => b.x + b.width));
  const maxY = Math.max(...valid.map((b) => b.y + b.height));
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

// The stall's own border, read off the drawing.
//
// There is no stall rectangle to look up. The layout is a PDF export and the
// borders are DASHED, so each one reaches the file as a few dozen unconnected
// dash segments - no element's box is a stall, which is why matching shapes by
// enclosure picks up whatever larger panel happens to contain the label.
//
// The border is still in the file, just as ink rather than as an object. And
// the nav grid already proved what that ink forms: rasterise it and every stall
// closes into a sealed pocket (150/150 - see LAYOUT_VIEW_CONTEXT.md). So the
// stall's exact extent is the pocket its label sits in. Flood-fill from the
// label, take the bounds of the region that comes back, and that is the border.
//
// Two details make it work at a cell fine enough to trace a border with:
//
//   dilate    at 0.75 units a dash GAP is wider than a cell, so a raw fill
//             leaks straight out into the aisle. Growing the ink by
//             OUTLINE_DILATE cells bridges gaps up to 3 units and seals the
//             stall - the same sealing the nav grid gets for free from its
//             coarse 2-unit cell.
//   snap      that dilation would then swell every box by a fixed margin, so
//             each edge of the filled region is walked back out to the first
//             row/column carrying real, undilated ink. The box lands on the
//             drawn line rather than on the dilation, which holds the error at
//             half a unit whatever the dash pattern.
//
// Rasterised at OUTLINE_CELL and kept for the overlay only; routing is untouched.
const OUTLINE_CELL = 0.75;         // SVG user units per cell
const OUTLINE_MAX_AREA = 0.03;     // a pocket bigger than this share of the sheet is the aisle, not a stall
const OUTLINE_DILATE = 2;          // cells of ink growth, to bridge the gaps between dashes
const OUTLINE_SNAP_REACH = 5;      // how far back out to hunt for the real line (OUTLINE_DILATE + 3)
const OUTLINE_SIZE_TOLERANCE = 0.2; // how far a trace may stand off the drawing's own scale
const OUTLINE_SIMPLIFY = 1.6;       // cells of slack when straightening a rasterised diagonal
const ZONE_CLUB_MAX_RADIUS = 60;    // cells (45 units); how far a zone will reach to join its stalls up - cheap now that closing is scoped to the zone's own bounding box

// Traces a set of grid cells into SVG path loops by cancelling shared edges:
// an edge between two cells of the same region appears twice, in opposite
// directions, and drops out - what survives is the boundary. Whatever shape the
// flood fill found comes out exactly, so a pentagon, an L or a stall with a
// pillar in it are no harder than a rectangle, which is just the simple case.
//
// A hole traces as its own loop with the opposite winding, so evenodd fill
// renders it correctly without any extra work.
const cellsToLoops = (cells, cols) => {
  const edges = new Map();
  const key = (a, b, c, d) => `${a},${b}:${c},${d}`;
  const add = (x1, y1, x2, y2) => {
    const rev = key(x2, y2, x1, y1);
    if (edges.has(rev)) { edges.delete(rev); return; } // interior edge, cancels
    edges.set(key(x1, y1, x2, y2), [x1, y1, x2, y2]);
  };
  cells.forEach((i) => {
    const r = (i / cols) | 0;
    const c = i - r * cols;
    add(c, r, c + 1, r);
    add(c + 1, r, c + 1, r + 1);
    add(c + 1, r + 1, c, r + 1);
    add(c, r + 1, c, r);
  });

  const from = new Map();
  edges.forEach(([x1, y1, x2, y2]) => {
    const k = `${x1},${y1}`;
    if (!from.has(k)) from.set(k, []);
    from.get(k).push([x2, y2]);
  });

  const loops = [];
  const used = new Set();
  edges.forEach(([x1, y1, x2, y2]) => {
    const start = key(x1, y1, x2, y2);
    if (used.has(start)) return;
    used.add(start);
    const pts = [[x1, y1]];
    let px = x1, py = y1, cx = x2, cy = y2;
    while (!(cx === x1 && cy === y1)) {
      pts.push([cx, cy]);
      const outs = from.get(`${cx},${cy}`) || [];
      const dx = cx - px, dy = cy - py;
      let next = null;
      for (let i = 0; i < outs.length; i += 1) {
        const [nx, ny] = outs[i];
        if (used.has(key(cx, cy, nx, ny))) continue;
        // Prefer carrying straight on, so a pinch point is not cut across.
        if (nx - cx === dx && ny - cy === dy) { next = outs[i]; break; }
        if (!next) next = outs[i];
      }
      if (!next) break;
      used.add(key(cx, cy, next[0], next[1]));
      px = cx; py = cy; cx = next[0]; cy = next[1];
    }
    if (pts.length >= 3) loops.push(pts);
  });
  return loops;
};

// Drop the vertices that sit on the line between their neighbours. A rasterised
// diagonal arrives as a staircase of single-cell steps; this collapses it back
// to the one straight edge the drawing actually has, while a true right angle
// stands too far off the chord to be dropped.
const simplifyLoop = (pts, tol) => {
  if (pts.length < 4) return pts;
  const dist2 = (p, a, b) => {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const L = dx * dx + dy * dy;
    if (!L) return (p[0] - a[0]) ** 2 + (p[1] - a[1]) ** 2;
    let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L;
    t = Math.max(0, Math.min(1, t));
    return (p[0] - a[0] - t * dx) ** 2 + (p[1] - a[1] - t * dy) ** 2;
  };
  const keep = new Array(pts.length).fill(false);
  keep[0] = true;
  keep[pts.length - 1] = true;
  const walk = (i, j) => {
    let best = -1;
    let bestD = tol * tol;
    for (let k = i + 1; k < j; k += 1) {
      const d = dist2(pts[k], pts[i], pts[j]);
      if (d > bestD) { bestD = d; best = k; }
    }
    if (best < 0) return;
    keep[best] = true;
    walk(i, best);
    walk(best, j);
  };
  walk(0, pts.length - 1);
  return pts.filter((_, i) => keep[i]);
};

// Clubs a scattered set of cells into one region by closing the gaps between
// them - grow the region, then shrink it back by the same amount. The growth
// bridges anything narrower than twice the radius; the shrink puts the outer
// boundary back where it started, so the outline still follows the shape of
// the group rather than ballooning around it.
//
// Two things keep this affordable at the canvas's real resolution (roughly a
// million cells for a full layout):
//
//   scoped   growth only ever touches the zone's own bounding box plus a
//            margin for the radius, never the whole canvas - a zone tucked
//            into one corner of a hall does not pay for the empty half of
//            the drawing.
//   cumulative   the region is grown one step at a time and checked after
//            each step, so the search stops the moment the pieces join
//            instead of re-running the whole grow+shrink cycle at every
//            radius from 1 up. The gap between two blocks across an aisle
//            typically closes within 5-15 steps; the shrink then undoes
//            exactly that many steps, once.
const clubCells = (cells, grid, maxRadius) => {
  if (!cells || cells.length === 0) return cells;

  let minC = grid.cols, maxC = -1, minR = grid.rows, maxR = -1;
  cells.forEach((i) => {
    const r = (i / grid.cols) | 0;
    const c = i - r * grid.cols;
    if (c < minC) minC = c;
    if (c > maxC) maxC = c;
    if (r < minR) minR = r;
    if (r > maxR) maxR = r;
  });

  const pad = maxRadius + 1;
  const c0 = Math.max(0, minC - pad);
  const r0 = Math.max(0, minR - pad);
  const c1 = Math.min(grid.cols - 1, maxC + pad);
  const r1 = Math.min(grid.rows - 1, maxR + pad);
  const subCols = c1 - c0 + 1;
  const subRows = r1 - r0 + 1;
  const n = subCols * subRows;

  const toSub = (i) => {
    const r = (i / grid.cols) | 0;
    const c = i - r * grid.cols;
    return (r - r0) * subCols + (c - c0);
  };
  const toGlobal = (j) => {
    const r = (j / subCols) | 0;
    const c = j - r * subCols;
    return (r + r0) * grid.cols + (c + c0);
  };

  let g = new Uint8Array(n);
  cells.forEach((i) => { g[toSub(i)] = 1; });

  const step = (src, grow) => {
    const out = new Uint8Array(n);
    for (let r = 0; r < subRows; r += 1) {
      for (let c = 0; c < subCols; c += 1) {
        const i = r * subCols + c;
        const right = c + 1 < subCols ? src[i + 1] : 0;
        const left = c > 0 ? src[i - 1] : 0;
        const down = r + 1 < subRows ? src[i + subCols] : 0;
        const up = r > 0 ? src[i - subCols] : 0;
        out[i] = grow
          ? (src[i] || right || left || down || up ? 1 : 0)
          : (src[i] && right && left && down && up ? 1 : 0);
      }
    }
    return out;
  };

  // Connected-component count, with an early exit the moment a second one
  // shows up - this runs after every growth step, so it never needs to be
  // exact past "more than one".
  const moreThanOneRegion = (arr) => {
    const seen = new Uint8Array(n);
    let comps = 0;
    for (let i = 0; i < n; i += 1) {
      if (!arr[i] || seen[i]) continue;
      comps += 1;
      if (comps > 1) return true;
      const stack = [i];
      seen[i] = 1;
      while (stack.length) {
        const cur = stack.pop();
        const r = (cur / subCols) | 0;
        const c = cur - r * subCols;
        if (c + 1 < subCols && arr[cur + 1] && !seen[cur + 1]) { seen[cur + 1] = 1; stack.push(cur + 1); }
        if (c > 0 && arr[cur - 1] && !seen[cur - 1]) { seen[cur - 1] = 1; stack.push(cur - 1); }
        if (r + 1 < subRows && arr[cur + subCols] && !seen[cur + subCols]) { seen[cur + subCols] = 1; stack.push(cur + subCols); }
        if (r > 0 && arr[cur - subCols] && !seen[cur - subCols]) { seen[cur - subCols] = 1; stack.push(cur - subCols); }
      }
    }
    return false;
  };

  let steps = 0;
  while (moreThanOneRegion(g) && steps < maxRadius) {
    g = step(g, true);
    steps += 1;
  }
  const stillSeparate = moreThanOneRegion(g);
  for (let k = 0; k < steps; k += 1) g = step(g, false);

  const out = [];
  for (let j = 0; j < n; j += 1) if (g[j]) out.push(toGlobal(j));

  // Closing bridges anything within reach; a gap wider than the cap is left as
  // more than one region. "One entire zone" cannot mean "usually one piece",
  // so the guaranteed fallback is a single convex fill over everything picked.
  if (stillSeparate) return hullFillCells(cells, grid);
  return out;
};

// A single convex region covering every given cell - the guaranteed fallback
// when closing cannot bridge a gap within its radius cap. It always comes back
// as exactly one region, which is the one promise a zone highlight has to keep;
// it can cover more than the stalls alone when two clusters sit far apart, the
// same trade closing already makes on a smaller scale.
const hullFillCells = (cells, grid) => {
  if (!cells || cells.length === 0) return cells;
  const pts = cells.map((i) => {
    const r = (i / grid.cols) | 0;
    return [i - r * grid.cols, r];
  });
  const sorted = pts.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower = [];
  sorted.forEach((p) => {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  });
  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i -= 1) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  upper.pop();
  lower.pop();
  const hull = lower.concat(upper);
  if (hull.length < 3) return cells;

  let minR = grid.rows;
  let maxR = -1;
  let minC = grid.cols;
  let maxC = -1;
  hull.forEach(([c, r]) => {
    if (r < minR) minR = r;
    if (r > maxR) maxR = r;
    if (c < minC) minC = c;
    if (c > maxC) maxC = c;
  });

  const out = [];
  for (let r = minR; r <= maxR; r += 1) {
    const xs = [];
    for (let i = 0; i < hull.length; i += 1) {
      const [x1, y1] = hull[i];
      const [x2, y2] = hull[(i + 1) % hull.length];
      if ((y1 <= r && y2 > r) || (y2 <= r && y1 > r)) {
        xs.push(x1 + ((r - y1) / (y2 - y1)) * (x2 - x1));
      }
    }
    xs.sort((a, b) => a - b);
    for (let k = 0; k + 1 < xs.length; k += 2) {
      const c0 = Math.max(minC, Math.ceil(xs[k]));
      const c1 = Math.min(maxC, Math.floor(xs[k + 1]));
      for (let c = c0; c <= c1; c += 1) out.push(r * grid.cols + c);
    }
  }
  return out.length ? out : cells;
};

// Cell region -> an SVG path in user units.// Cell region -> an SVG path in user units.
const cellsToPath = (cells, grid) => {
  if (!cells || cells.length === 0) return null;
  const loops = cellsToLoops(cells, grid.cols);
  if (loops.length === 0) return null;
  const parts = [];
  loops.forEach((loop) => {
    const pts = simplifyLoop(loop, OUTLINE_SIMPLIFY);
    if (pts.length < 3) return;
    const at = (pt) => `${(grid.ox + pt[0] * grid.cell).toFixed(2)} ${(grid.oy + pt[1] * grid.cell).toFixed(2)}`;
    parts.push(`M${at(pts[0])}` + pts.slice(1).map((pt) => `L${at(pt)}`).join("") + "Z");
  });
  return parts.length ? parts.join("") : null;
};

const findBoothOutlines = (svgElement, elementsByBooth) => {
  const outlines = new Map();
  if (!svgElement || !elementsByBooth || elementsByBooth.size === 0) return outlines;

  const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
  const ox = (vb && vb.x) || 0;
  const oy = (vb && vb.y) || 0;
  const vw = (vb && vb.width) || 792;
  const vh = (vb && vb.height) || 612;

  const cols = Math.max(1, Math.ceil(vw / OUTLINE_CELL));
  const rows = Math.max(1, Math.ceil(vh / OUTLINE_CELL));
  const ink = new Uint8Array(cols * rows);

  const ctmCache = new Map();
  const ctmOf = (el) => {
    const parent = el.parentNode;
    let base = ctmCache.get(parent);
    if (base === undefined) {
      base = parent ? matrixToSvgUserSpace(parent) : null;
      ctmCache.set(parent, base);
    }
    const own = parseTransformAttr(el.getAttribute("transform"));
    if (own === null) return matrixToSvgUserSpace(el);
    return base ? matMul(base, own) : own;
  };

  const mark = (x0, y0, x1, y1) => {
    const steps = Math.ceil(Math.hypot(x1 - x0, y1 - y0) / (OUTLINE_CELL * 0.4)) + 1;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const c = Math.floor((x0 + (x1 - x0) * t - ox) / OUTLINE_CELL);
      const r = Math.floor((y0 + (y1 - y0) * t - oy) / OUTLINE_CELL);
      if (c >= 0 && r >= 0 && c < cols && r < rows) ink[r * cols + c] = 1;
    }
  };

  let paths = [];
  try {
    paths = Array.from(svgElement.querySelectorAll("path"));
  } catch (e) {
    return outlines;
  }
  paths.forEach((el) => {
    if (isOverlayNode(el) || inTextLayer(el)) return;
    const d = el.getAttribute("d");
    if (!d) return;
    const m = ctmOf(el);
    pathSegments(d).forEach(([x0, y0, x1, y1]) => {
      let p0x = x0, p0y = y0, p1x = x1, p1y = y1;
      if (m) {
        p0x = m.a * x0 + m.c * y0 + m.e; p0y = m.b * x0 + m.d * y0 + m.f;
        p1x = m.a * x1 + m.c * y1 + m.e; p1y = m.b * x1 + m.d * y1 + m.f;
      }
      mark(p0x, p0y, p1x, p1y);
    });
  });

  let blocked = ink;
  for (let pass = 0; pass < OUTLINE_DILATE; pass += 1) {
    const grown = new Uint8Array(cols * rows);
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        const i = r * cols + c;
        if (!blocked[i]) continue;
        grown[i] = 1;
        if (c + 1 < cols) grown[i + 1] = 1;
        if (c > 0) grown[i - 1] = 1;
        if (r + 1 < rows) grown[i + cols] = 1;
        if (r > 0) grown[i - cols] = 1;
      }
    }
    blocked = grown;
  }

  const stamp = new Int32Array(cols * rows);
  const maxCells = Math.floor((cols * rows) * OUTLINE_MAX_AREA);
  const stack = [];

  const freeCellNear = (c0, r0) => {
    for (let ring = 0; ring <= 6; ring += 1) {
      for (let dr = -ring; dr <= ring; dr += 1) {
        for (let dc = -ring; dc <= ring; dc += 1) {
          if (ring > 0 && Math.abs(dr) !== ring && Math.abs(dc) !== ring) continue;
          const c = c0 + dc, r = r0 + dr;
          if (c < 0 || r < 0 || c >= cols || r >= rows) continue;
          if (!blocked[r * cols + c]) return r * cols + c;
        }
      }
    }
    return -1;
  };

  const pocketBox = (seed, id) => {
    let minC = cols, maxC = -1, minR = rows, maxR = -1, size = 0;
    const visited = [];
    stack.length = 0;
    stack.push(seed);
    stamp[seed] = id;
    while (stack.length) {
      const cur = stack.pop();
      visited.push(cur);
      size += 1;
      if (size > maxCells) return null;
      const r = (cur / cols) | 0;
      const c = cur - r * cols;
      if (c < minC) minC = c;
      if (c > maxC) maxC = c;
      if (r < minR) minR = r;
      if (r > maxR) maxR = r;
      if (c + 1 < cols && !blocked[cur + 1] && stamp[cur + 1] !== id) { stamp[cur + 1] = id; stack.push(cur + 1); }
      if (c - 1 >= 0 && !blocked[cur - 1] && stamp[cur - 1] !== id) { stamp[cur - 1] = id; stack.push(cur - 1); }
      if (r + 1 < rows && !blocked[cur + cols] && stamp[cur + cols] !== id) { stamp[cur + cols] = id; stack.push(cur + cols); }
      if (r - 1 >= 0 && !blocked[cur - cols] && stamp[cur - cols] !== id) { stamp[cur - cols] = id; stack.push(cur - cols); }
    }
    if (maxC < minC || maxR < minR) return null;

    const colHasInk = (c) => {
      for (let r = minR; r <= maxR; r += 1) if (ink[r * cols + c]) return true;
      return false;
    };
    const rowHasInk = (r) => {
      for (let c = minC; c <= maxC; c += 1) if (ink[r * cols + c]) return true;
      return false;
    };
    let left = minC - 1;
    let right = maxC + 1;
    let top = minR - 1;
    let bottom = maxR + 1;
    for (let d = 1; d <= OUTLINE_SNAP_REACH; d += 1) {
      if (minC - d >= 0 && colHasInk(minC - d)) { left = minC - d; break; }
    }
    for (let d = 1; d <= OUTLINE_SNAP_REACH; d += 1) {
      if (maxC + d < cols && colHasInk(maxC + d)) { right = maxC + d; break; }
    }
    for (let d = 1; d <= OUTLINE_SNAP_REACH; d += 1) {
      if (minR - d >= 0 && rowHasInk(minR - d)) { top = minR - d; break; }
    }
    for (let d = 1; d <= OUTLINE_SNAP_REACH; d += 1) {
      if (maxR + d < rows && rowHasInk(maxR + d)) { bottom = maxR + d; break; }
    }

    return {
      x: ox + (left + 0.5) * OUTLINE_CELL,
      y: oy + (top + 0.5) * OUTLINE_CELL,
      width: (right - left) * OUTLINE_CELL,
      height: (bottom - top) * OUTLINE_CELL,
      cells: visited,
    };
  };

  const printedSizes = [];
  try {
    Array.from(svgElement.querySelectorAll("text")).forEach((el) => {
      if (isOverlayNode(el)) return;
      const m = String(el.textContent || "")
        .replace(/\s+/g, "")
        .match(/^(\d+(?:\.\d+)?)[xX×](\d+(?:\.\d+)?)\((\d+(?:\.\d+)?)\)$/);
      if (!m) return;
      const box = elementBoxInSvgSpace(el);
      if (!box) return;
      printedSizes.push({
        area: Number(m[3]) || Number(m[1]) * Number(m[2]),
        w: Number(m[1]),
        h: Number(m[2]),
        cx: box.x + box.width / 2,
        cy: box.y + box.height / 2,
      });
    });
  } catch (e) { }

  const printedFor = (box) => {
    let best = null;
    let bestD = Infinity;
    for (let i = 0; i < printedSizes.length; i += 1) {
      const d = printedSizes[i];
      if (d.cx < box.x || d.cx > box.x + box.width) continue;
      if (d.cy < box.y || d.cy > box.y + box.height) continue;
      const dist = Math.hypot(d.cx - (box.x + box.width / 2), d.cy - (box.y + box.height / 2));
      if (dist < bestD) { bestD = dist; best = d; }
    }
    return best;
  };

  let id = 0;
  const stats = [];
  const cellsByBooth = new Map();
  elementsByBooth.forEach((els, name) => {
    const label = unionBoxes(els.map(elementBoxInSvgSpace));
    if (!label) return;
    id += 1;
    const c0 = Math.floor((label.x + label.width / 2 - ox) / OUTLINE_CELL);
    const r0 = Math.floor((label.y + label.height / 2 - oy) / OUTLINE_CELL);
    const seed = freeCellNear(c0, r0);
    if (seed < 0) return;
    const box = pocketBox(seed, id);
    if (!box) { stats.push({ booth: name, result: "escaped to aisle" }); return; }
    const holdsLabel =
      label.x >= box.x - 1 &&
      label.y >= box.y - 1 &&
      label.x + label.width <= box.x + box.width + 1 &&
      label.y + label.height <= box.y + box.height + 1;
    if (!holdsLabel) { stats.push({ booth: name, result: "lost its label" }); return; }
    outlines.set(name, box);
    cellsByBooth.set(name, box.cells);
    const printed = printedFor(box);
    stats.push({
      booth: name,
      result: `${box.width.toFixed(1)} x ${box.height.toFixed(1)}`,
      printed: printed ? `${printed.w}x${printed.h}(${printed.area})` : null,
      scale: printed && printed.area > 0
        ? Math.sqrt((box.width * box.height) / printed.area)
        : null,
      label,
    });
  });

  const hallOf = (name) => {
    const m = String(name).match(/^H(\d+)/i);
    return m ? m[1] : "?";
  };
  const median = (arr) => {
    const sorted = arr.slice().sort((a, b) => a - b);
    return sorted[Math.floor(sorted.length / 2)];
  };

  const scalesByHall = new Map();
  stats.forEach((r) => {
    if (!(r.scale > 0)) return;
    const hall = hallOf(r.booth);
    if (!scalesByHall.has(hall)) scalesByHall.set(hall, []);
    scalesByHall.get(hall).push(r.scale);
  });
  const allScales = stats.map((r) => r.scale).filter((v) => v > 0);
  const globalK = allScales.length >= 4 ? median(allScales) : null;

  if (globalK || scalesByHall.size > 0) {
    stats.forEach((r) => {
      if (!r.scale || !r.printed) return;
      const hall = hallOf(r.booth);
      const hallScales = scalesByHall.get(hall) || [];
      const K = hallScales.length >= 4 ? median(hallScales) : globalK;
      if (!K) return;

      const off = Math.abs(r.scale - K) / K;
      if (off <= OUTLINE_SIZE_TOLERANCE) return;

      const box = outlines.get(r.booth);
      const printed = box && printedFor(box);
      if (!box || !printed) return;

      const landscape = box.width >= box.height;
      const w = (landscape ? Math.max(printed.w, printed.h) : Math.min(printed.w, printed.h)) * K;
      const h = (landscape ? Math.min(printed.w, printed.h) : Math.max(printed.w, printed.h)) * K;

      const fixed = {
        x: printed.cx - w / 2,
        y: printed.cy - h / 2,
        width: w,
        height: h,
      };
      const l = r.label;
      const covers =
        l.x >= fixed.x - 2 && l.y >= fixed.y - 2 &&
        l.x + l.width <= fixed.x + fixed.width + 2 &&
        l.y + l.height <= fixed.y + fixed.height + 2;
      if (!covers) { r.result += " (off-size, kept)"; return; }

      outlines.set(r.booth, fixed);
      cellsByBooth.delete(r.booth);
    });
  }

  try {
    window.__boothOutlines = {
      cell: OUTLINE_CELL,
      grid: `${cols} x ${rows}`,
      traced: outlines.size,
      fellBack: elementsByBooth.size - outlines.size,
      booths: stats,
    };
  } catch (e) { }

  outlines.grid = { cols, rows, ox, oy, cell: OUTLINE_CELL };
  outlines.cells = cellsByBooth;
  return outlines;
};

// Fallback for a booth whose outline could not be found. Grows the label box
// into a booth-cell-sized target so the focus ring reads as the stall rather
// than as the text.
const padBox = (box, padX = 7, padY = 7, minW = 24, minH = 15) => {
  if (!box) return null;
  let { x, y, width, height } = box;
  x -= padX; y -= padY; width += padX * 2; height += padY * 2;
  if (width < minW) { x -= (minW - width) / 2; width = minW; }
  if (height < minH) { y -= (minH - height) / 2; height = minH; }
  return { x, y, width, height };
};

// ─── Walkable navigation grid ───────────────────────────────────────────
// Routing cannot use a booth-to-booth graph: every edge in one is a straight
// line, so the shortest path cuts diagonally through the stalls. Nor can it use
// booth label boxes as obstacles - a label is a ~10x4 unit sliver sitting inside
// a stall that may be 30x25, leaving most of the stall walkable.
//
// Instead the drawing's own line work is rasterised as walls. Every stall in
// this layout is drawn as a closed (if dashed) outline, so once the ink is
// blocked each stall becomes a sealed pocket and the largest remaining free
// region *is* the aisle network. Routing is confined to that region, which
// makes passing through a stall structurally impossible rather than merely
// expensive. Measured on the ELECRAMA layout: 150/150 stalls sealed, 10/10
// sample routes entirely within the aisles.

const NAV_CELL = 2;        // SVG user units per cell
const MIN_INK_SEG = 1.5;   // shorter strokes are glyph outlines, not structure
const TURN_COST = 2.5;     // without this the optimal path is a staircase
const HUG_COST = 1.5;      // keeps the line off the walls, down the aisle centre

// Layers holding text rendered as outlines. Their glyph strokes would pepper
// the aisles with phantom obstacles.
const TEXT_LAYERS = new Set(["Stall No", "Stall Size", "Area"]);

// 4-directional only: a diagonal step could slip through the corner gap where
// two stalls touch.
const NAV_DIRS = [
  { dx: 1, dy: 0 },
  { dx: -1, dy: 0 },
  { dx: 0, dy: 1 },
  { dx: 0, dy: -1 },
];

class MinHeap {
  constructor() { this.items = []; this.costs = []; }
  get size() { return this.items.length; }
  push(item, cost) {
    this.items.push(item); this.costs.push(cost);
    let i = this.items.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (this.costs[p] <= this.costs[i]) break;
      this.swap(p, i); i = p;
    }
  }
  pop() {
    const top = this.items[0];
    const li = this.items.pop();
    const lc = this.costs.pop();
    if (this.items.length > 0) {
      this.items[0] = li; this.costs[0] = lc;
      let i = 0;
      for (; ;) {
        const l = 2 * i + 1;
        const r = l + 1;
        let s = i;
        if (l < this.items.length && this.costs[l] < this.costs[s]) s = l;
        if (r < this.items.length && this.costs[r] < this.costs[s]) s = r;
        if (s === i) break;
        this.swap(s, i); i = s;
      }
    }
    return top;
  }
  swap(a, b) {
    const ti = this.items[a]; this.items[a] = this.items[b]; this.items[b] = ti;
    const tc = this.costs[a]; this.costs[a] = this.costs[b]; this.costs[b] = tc;
  }
}

const matMul = (m, n) => ({
  a: m.a * n.a + m.c * n.b,
  b: m.b * n.a + m.d * n.b,
  c: m.a * n.c + m.c * n.d,
  d: m.b * n.c + m.d * n.d,
  e: m.a * n.e + m.c * n.f + m.e,
  f: m.b * n.e + m.d * n.f + m.f,
});

const IDENTITY = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

// Only the forms this exporter emits, plus the common shorthands.
const parseTransformAttr = (str) => {
  if (!str) return IDENTITY;
  let out = IDENTITY;
  const re = /(matrix|translate|scale)\(([^)]*)\)/g;
  let m;
  let matched = false;
  while ((m = re.exec(str)) !== null) {
    const v = m[2].trim().split(/[\s,]+/).map(Number);
    if (v.some(Number.isNaN)) return null;
    let next = null;
    if (m[1] === "matrix" && v.length >= 6) {
      next = { a: v[0], b: v[1], c: v[2], d: v[3], e: v[4], f: v[5] };
    } else if (m[1] === "translate") {
      next = { a: 1, b: 0, c: 0, d: 1, e: v[0] || 0, f: v.length > 1 ? v[1] : 0 };
    } else if (m[1] === "scale") {
      next = { a: v[0] || 1, b: 0, c: 0, d: v.length > 1 ? v[1] : (v[0] || 1), e: 0, f: 0 };
    }
    if (!next) return null;
    out = matMul(out, next);
    matched = true;
  }
  return matched ? out : (str.trim() ? null : IDENTITY);
};

// Straight-line vertices of a path. Curves are only used for glyphs and arrow
// heads in this drawing, so treating their endpoints as vertices is enough.
const pathSegments = (d) => {
  const cmds = d.match(/[MmLlHhVvCcSsQqTtAaZz][^MmLlHhVvCcSsQqTtAaZz]*/g);
  if (!cmds) return [];
  const segs = [];
  let cx = 0, cy = 0, sx = 0, sy = 0, prev = null;
  const emit = (x, y) => { if (prev) segs.push([prev.x, prev.y, x, y]); prev = { x, y }; };
  for (const c of cmds) {
    const op = c[0];
    const a = c.slice(1).match(/-?\d*\.?\d+(?:e-?\d+)?/gi);
    const n = a ? a.map(Number) : [];
    if (op === "M" || op === "m") {
      for (let i = 0; i + 1 < n.length; i += 2) {
        if (op === "M") { cx = n[i]; cy = n[i + 1]; } else { cx += n[i]; cy += n[i + 1]; }
        if (i === 0) { prev = null; sx = cx; sy = cy; }
        emit(cx, cy);
      }
    } else if (op === "L" || op === "l") {
      for (let i = 0; i + 1 < n.length; i += 2) {
        if (op === "L") { cx = n[i]; cy = n[i + 1]; } else { cx += n[i]; cy += n[i + 1]; }
        emit(cx, cy);
      }
    } else if (op === "H" || op === "h") {
      for (const v of n) { cx = op === "H" ? v : cx + v; emit(cx, cy); }
    } else if (op === "V" || op === "v") {
      for (const v of n) { cy = op === "V" ? v : cy + v; emit(cx, cy); }
    } else if (op === "Z" || op === "z") {
      emit(sx, sy);
    } else {
      prev = null; // curve: break the run rather than guess at its shape
    }
  }
  return segs;
};

const inTextLayer = (el) => {
  for (let p = el.parentNode; p && p.getAttribute; p = p.parentNode) {
    const label = p.getAttribute("inkscape:label");
    if (label && TEXT_LAYERS.has(label)) return true;
    if (p.tagName && p.tagName.toLowerCase() === "svg") break;
  }
  return false;
};

// Rasterise the drawing's line work into a blocked-cell grid.
const buildNavGrid = (svgElement, viewBox) => {
  const { x: ox, y: oy, width: vw, height: vh } = viewBox;
  const cols = Math.max(1, Math.ceil(vw / NAV_CELL));
  const rows = Math.max(1, Math.ceil(vh / NAV_CELL));
  const blocked = new Uint8Array(cols * rows);

  const ctmCache = new Map();
  const ctmOf = (el) => {
    // Matrix(path) = Matrix(parent) x transform(path), all in root USER space -
    // see matrixToSvgUserSpace: getCTM() would return viewport pixels once the
    // svg scales to its container, which would misplace every wall. Caching per
    // parent means a handful of matrix reads instead of one per path.
    const parent = el.parentNode;
    let base = ctmCache.get(parent);
    if (base === undefined) {
      base = parent ? matrixToSvgUserSpace(parent) : null;
      ctmCache.set(parent, base);
    }
    const own = parseTransformAttr(el.getAttribute("transform"));
    if (own === null) {
      return matrixToSvgUserSpace(el);
    }
    return base ? matMul(base, own) : own;
  };

  let paths = [];
  try {
    paths = Array.from(svgElement.querySelectorAll("path"));
  } catch (e) {
    paths = [];
  }

  const mark = (x0, y0, x1, y1) => {
    const steps = Math.ceil(Math.hypot(x1 - x0, y1 - y0) / (NAV_CELL * 0.4)) + 1;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const c = Math.floor((x0 + (x1 - x0) * t - ox) / NAV_CELL);
      const r = Math.floor((y0 + (y1 - y0) * t - oy) / NAV_CELL);
      if (c >= 0 && r >= 0 && c < cols && r < rows) blocked[r * cols + c] = 1;
    }
  };

  paths.forEach((el) => {
    if (isOverlayNode(el) || inTextLayer(el)) return;
    const d = el.getAttribute("d");
    if (!d) return;
    const m = ctmOf(el);
    pathSegments(d).forEach(([x0, y0, x1, y1]) => {
      let p0x = x0, p0y = y0, p1x = x1, p1y = y1;
      if (m) {
        p0x = m.a * x0 + m.c * y0 + m.e; p0y = m.b * x0 + m.d * y0 + m.f;
        p1x = m.a * x1 + m.c * y1 + m.e; p1y = m.b * x1 + m.d * y1 + m.f;
      }
      if (Math.hypot(p1x - p0x, p1y - p0y) < MIN_INK_SEG) return;
      mark(p0x, p0y, p1x, p1y);
    });
  });

  // Flood fill the free space. Stalls are sealed pockets; the biggest region
  // left over is the aisle network, and routing never leaves it.
  const comp = new Int32Array(cols * rows).fill(-1);
  const sizes = [];
  const stack = [];
  for (let seed = 0; seed < cols * rows; seed++) {
    if (blocked[seed] || comp[seed] !== -1) continue;
    const id = sizes.length;
    let size = 0;
    stack.length = 0;
    stack.push(seed);
    comp[seed] = id;
    while (stack.length) {
      const cur = stack.pop();
      size++;
      const r = (cur / cols) | 0;
      const c = cur - r * cols;
      if (c + 1 < cols && !blocked[cur + 1] && comp[cur + 1] === -1) { comp[cur + 1] = id; stack.push(cur + 1); }
      if (c - 1 >= 0 && !blocked[cur - 1] && comp[cur - 1] === -1) { comp[cur - 1] = id; stack.push(cur - 1); }
      if (r + 1 < rows && !blocked[cur + cols] && comp[cur + cols] === -1) { comp[cur + cols] = id; stack.push(cur + cols); }
      if (r - 1 >= 0 && !blocked[cur - cols] && comp[cur - cols] === -1) { comp[cur - cols] = id; stack.push(cur - cols); }
    }
    sizes.push(size);
  }
  let main = -1;
  let best = -1;
  sizes.forEach((s, i) => { if (s > best) { best = s; main = i; } });

  // Cells adjacent to a wall cost more, so routes run down aisle centres.
  const hugging = new Uint8Array(cols * rows);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      if (comp[i] !== main) continue;
      for (let d = 0; d < NAV_DIRS.length && !hugging[i]; d++) {
        const nr = r + NAV_DIRS[d].dy;
        const nc = c + NAV_DIRS[d].dx;
        if (nr < 0 || nc < 0 || nr >= rows || nc >= cols || comp[nr * cols + nc] !== main) hugging[i] = 1;
      }
    }
  }

  return { cols, rows, blocked, comp, main, hugging, ox, oy, aisleCells: best };
};

// A booth centre sits inside its own sealed stall, so step out to the closest
// cell that is actually on the aisle network - the stall's opening.
const nearestAisleCell = (grid, x, y, maxRadius = 60) => {
  const { cols, rows, comp, main } = grid;
  const col0 = Math.floor((x - grid.ox) / NAV_CELL);
  const row0 = Math.floor((y - grid.oy) / NAV_CELL);
  for (let radius = 0; radius <= maxRadius; radius++) {
    let best = null;
    let bestD = Infinity;
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        if (radius > 0 && Math.max(Math.abs(dr), Math.abs(dc)) !== radius) continue;
        const c = col0 + dc;
        const r = row0 + dr;
        if (c < 0 || r < 0 || c >= cols || r >= rows) continue;
        if (comp[r * cols + c] !== main) continue;
        const d = dr * dr + dc * dc;
        if (d < bestD) { bestD = d; best = { col: c, row: r }; }
      }
    }
    if (best) return best;
  }
  return null;
};

// Dijkstra over (cell, heading), confined to the aisle network. Heading is part
// of the state so a turn can be charged for.
const dijkstraOnGrid = (grid, start, goal) => {
  const { cols, rows, comp, main, hugging } = grid;
  const stateCount = cols * rows * 4;

  const dist = new Float64Array(stateCount).fill(Infinity);
  const prev = new Int32Array(stateCount).fill(-1);
  const settled = new Uint8Array(stateCount);

  const startCell = start.row * cols + start.col;
  const goalCell = goal.row * cols + goal.col;
  if (comp[startCell] !== main || comp[goalCell] !== main) return null;

  const heap = new MinHeap();
  for (let d = 0; d < 4; d++) {
    dist[startCell * 4 + d] = 0;
    heap.push(startCell * 4 + d, 0);
  }

  let goalState = -1;
  while (heap.size > 0) {
    const state = heap.pop();
    if (settled[state]) continue;
    settled[state] = 1;

    const cell = state >> 2;
    const dir = state & 3;
    if (cell === goalCell) { goalState = state; break; }

    const row = (cell / cols) | 0;
    const col = cell - row * cols;

    for (let nd = 0; nd < 4; nd++) {
      const nc = col + NAV_DIRS[nd].dx;
      const nr = row + NAV_DIRS[nd].dy;
      if (nc < 0 || nr < 0 || nc >= cols || nr >= rows) continue;
      const ncell = nr * cols + nc;
      if (comp[ncell] !== main) continue;

      const nstate = ncell * 4 + nd;
      if (settled[nstate]) continue;

      const step = 1 + (hugging[ncell] ? HUG_COST : 0) + (nd === dir ? 0 : TURN_COST);
      const alt = dist[state] + step;
      if (alt < dist[nstate]) {
        dist[nstate] = alt;
        prev[nstate] = state;
        heap.push(nstate, alt);
      }
    }
  }

  if (goalState < 0) return null;

  const cells = [];
  for (let s = goalState; s !== -1; s = prev[s]) {
    const cell = s >> 2;
    if (cells.length === 0 || cells[cells.length - 1] !== cell) cells.push(cell);
    if (prev[s] === -1) break;
  }
  cells.reverse();
  return cells.map((cell) => {
    const row = (cell / cols) | 0;
    return { col: cell - row * cols, row };
  });
};

// Collapse collinear runs so the drawn polyline has corners, not 400 points.
const simplifyCells = (cells) => {
  if (!cells || cells.length < 3) return cells || [];
  const out = [cells[0]];
  for (let i = 1; i < cells.length - 1; i++) {
    const a = out[out.length - 1];
    const b = cells[i];
    const c = cells[i + 1];
    const straight = (b.col - a.col) * (c.row - b.row) === (b.row - a.row) * (c.col - b.col);
    if (!straight) out.push(b);
  }
  out.push(cells[cells.length - 1]);
  return out;
};

const cellToPoint = (grid, cell) => ({
  x: grid.ox + (cell.col + 0.5) * NAV_CELL,
  y: grid.oy + (cell.row + 0.5) * NAV_CELL,
});

const SvgCanvas = ({
  layout,
  viewFilter = "stall", // "stall" | "zone" | "hall"
  dataFilters = ["service_partner", "exhibitor_orders", "daily_routine"],
  onViewFilterChange,
  onDataFilterChange,
  hallZoneMasterRows = [],
  servicePartnerRows = [],
  exhibitorOrderRows = [],
  dailyRoutineRows = [],
  filterHallId,
  filterBoothId,
  onFilterHallChange,
  onFilterBoothChange,
  onSvgDataExtracted,
  zones = [],
  selectedZoneId = "",
  onZoneChange,
  isZoneLoading = false,
  zoneStalls = [],
  statusCounts = {},
  isStatusLoading = false,
  taskRows = [],
  exhibitorRows = [],
  isTaskLoading = false,
  taskLoadError = null,
  services = [],
  selectedServiceId = "",
  onServiceChange,
  // Verification state per deliverable, keyed by ExhVendorPlanDtlID. Only the
  // Organizer can move it, the same rule the vendor task screen applies
  verificationById = {},
  onOpenTaskDetail,
  onOpenManualForm,
}) => {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const tooltipContentRef = useRef(null);
  const tooltipDotRef = useRef(null);

  // Multi-mode filter local states (supporting controlled & uncontrolled)
  const [internalViewFilter, setInternalViewFilter] = useState("stall");
  const [internalDataFilters, setInternalDataFilters] = useState(["service_partner", "exhibitor_orders", "daily_routine"]);
  const [isDataFilterOpen, setIsDataFilterOpen] = useState(false);

  const currentViewFilter = viewFilter || internalViewFilter;
  const currentDataFilters = dataFilters || internalDataFilters;

  const handleViewFilterSelect = (newView) => {
    setInternalViewFilter(newView);
    if (onViewFilterChange) onViewFilterChange(newView);
  };

  const handleToggleDataFilter = (filterKey) => {
    let next;
    if (currentDataFilters.includes(filterKey)) {
      if (currentDataFilters.length === 1) return; // keep at least 1 active
      next = currentDataFilters.filter((k) => k !== filterKey);
    } else {
      next = [...currentDataFilters, filterKey];
    }
    setInternalDataFilters(next);
    if (onDataFilterChange) onDataFilterChange(next);
  };

  // Route state
  const [routeFromBoothId, setRouteFromBoothId] = useState("");
  const [routeToBoothId, setRouteToBoothId] = useState("");
  const [selectedBooth, setSelectedBooth] = useState(null);
  const [routeError, setRouteError] = useState("");

  const boothNodesRef = useRef({});
  const navGridRef = useRef(null);
  const nodeBoothMapRef = useRef({});
  const clipIndexRef = useRef({ elementsByClip: new Map(), clipPathDefsById: new Map(), elementsByBoothName: new Map(), textElementsByContent: new Map() });

  const transform = useRef({
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    isDragging: false,
    hasDragged: false,
    dragStart: { x: 0, y: 0 },
    dragInitial: { x: 0, y: 0 }
  });

  const zoomLabelRef = useRef(null);
  const willChangeTimeoutRef = useRef(null);
  // Bumped once the booth dots exist in the DOM, so the effect that colours
  // them for the service filter can run after they are created rather than
  // racing the parse.
  const [dotsVersion, setDotsVersion] = useState(0);
  const [isProcessingFilter, setIsProcessingFilter] = useState(false);
  const [filterMessage, setFilterMessage] = useState("");
  const [focusError, setFocusError] = useState("");

  // Halls/booths read out of the drawing. These drive the dropdowns.
  const [svgData, setSvgData] = useState({ halls: [], booths: [] });
  const boothElementsRef = useRef(new Map());
  // Booth name -> the stall border found in the drawing, in root user space.
  const boothOutlinesRef = useRef(new Map());

  // Size the canvas to the drawing's own aspect ratio. Letterboxing a 1.29:1
  // plan inside a 3.3:1 box can only ever fill ~39% of it; matching the box to
  // the drawing means the map itself has no dead space at all.
  const canvasWrapRef = useRef(null);
  const [svgAspect, setSvgAspect] = useState(null);
  const [canvasBox, setCanvasBox] = useState({ width: null, height: 500 });

  // Once the user pans or zooms, stop re-imposing the default view on them.
  const hasUserAdjustedRef = useRef(false);
  const defaultViewAppliedRef = useRef(false);

  const booths = svgData.booths;
  const halls = svgData.halls;

  const STALL_TASK_STATUS = {
    1: "Completed",
    2: "InProgress",
    3: "Pending",
    4: "Delayed",
    5: null, // unallocated - drawn dimmed
  };

  const isServicePartnerActive = currentDataFilters.includes("service_partner");
  const isExhibitorOrdersActive = currentDataFilters.includes("exhibitor_orders");
  const isDailyRoutineActive = currentDataFilters.includes("daily_routine");

  const normalizeKey = (str) => String(str || "").trim().toLowerCase();

  // Effective datasets combining explicit props and fallbacks
  const effectiveServicePartnerRows = (servicePartnerRows && servicePartnerRows.length > 0) ? servicePartnerRows : (exhibitorRows || []);
  const effectiveExhibitorOrderRows = (exhibitorOrderRows && exhibitorOrderRows.length > 0) ? exhibitorOrderRows : [];
  const effectiveDailyRoutineRows = (dailyRoutineRows && dailyRoutineRows.length > 0) ? dailyRoutineRows : [];

  // 1. Status per stall (for Stall View)
  const { statusByBooth, stallStatsDetail } = useMemo(() => {
    const wanted = selectedServiceId ? String(selectedServiceId) : null;
    const stallTasks = new Map();

    if (isServicePartnerActive) {
      effectiveServicePartnerRows.forEach((r) => {
        const key = normalizeBoothKey(r.StallName);
        if (!key) return;
        const svcId = firstValue(r.ServiceID);
        if (wanted && svcId != null && String(svcId) !== wanted) return;

        let statusStr = null;
        const rawStat = firstValue(r.StallTaskStatus);
        if (typeof rawStat === "string") {
          statusStr = rawStat;
        } else if (typeof rawStat === "number") {
          statusStr = STALL_TASK_STATUS[rawStat] || null;
        }
        if (!statusStr) statusStr = "Pending";

        if (!stallTasks.has(key)) stallTasks.set(key, []);
        stallTasks.get(key).push({
          status: statusStr,
          serviceId: svcId,
          source: "service_partner",
          overdue: r.Overdue,
          vendor: r.VendorCompanyName || r.vendorPersonName,
        });
      });
    }

    if (isExhibitorOrdersActive) {
      effectiveExhibitorOrderRows.forEach((r) => {
        const key = normalizeBoothKey(r.Stall || r.StallName);
        if (!key) return;
        const svcId = r.ServiceID;
        if (wanted && svcId != null && String(svcId) !== wanted) return;

        let statusStr = "Pending";
        const isCompleted = (r.IsVerifyByHallManager === 1 || r.IsVerifyByHallManager === true) &&
                            (r.IsVerifyByOrganizer === 1 || r.IsVerifyByOrganizer === true);
        if (isCompleted) {
          statusStr = "Completed";
        } else if (r.WorkStatusID === 2 || r.WorkStatusID === true || r.Status === "InProgress" || r.IsVerifyByHallManager === 1 || r.IsVerifyByHallManager === true) {
          statusStr = "InProgress";
        } else if (r.HMWorkupdationID || r.Service || (r.Status && r.Status !== "NA")) {
          statusStr = "Pending";
        }

        if (!stallTasks.has(key)) stallTasks.set(key, []);
        stallTasks.get(key).push({
          status: statusStr,
          serviceId: svcId,
          source: "exhibitor_orders",
          serviceName: r.Service,
          vendor: r.VendorName,
        });
      });
    }

    const map = new Map();
    stallTasks.forEach((tasks, key) => {
      if (!tasks || tasks.length === 0) {
        map.set(key, null);
        return;
      }
      const hasDelayed = tasks.some((t) => t.status === "Delayed");
      if (hasDelayed) {
        map.set(key, "Delayed");
        return;
      }
      const allCompleted = tasks.every((t) => t.status === "Completed");
      if (allCompleted) {
        map.set(key, "Completed");
        return;
      }
      const allPending = tasks.every((t) => t.status === "Pending");
      if (allPending) {
        map.set(key, "Pending");
        return;
      }
      map.set(key, "InProgress");
    });

    return { statusByBooth: map, stallStatsDetail: stallTasks };
  }, [isServicePartnerActive, isExhibitorOrdersActive, effectiveServicePartnerRows, effectiveExhibitorOrderRows, selectedServiceId]);

  // 2. Status per Zone (for Zone Status View)
  const zoneStatusMap = useMemo(() => {
    const wanted = selectedServiceId ? String(selectedServiceId) : null;
    const zoneTasks = new Map();

    if (isServicePartnerActive) {
      effectiveServicePartnerRows.forEach((r) => {
        const zName = firstValue(r.ZoneName) || firstValue(r.Zone);
        if (!zName) return;
        const key = normalizeKey(zName);
        const svcId = firstValue(r.ServiceID);
        if (wanted && svcId != null && String(svcId) !== wanted) return;

        const stat = r.ZoneStatus || r.StallTaskStatus || "Pending";
        if (!zoneTasks.has(key)) zoneTasks.set(key, []);
        zoneTasks.get(key).push(stat);
      });
    }

    if (isExhibitorOrdersActive) {
      effectiveExhibitorOrderRows.forEach((r) => {
        const zName = r.Zone || r.ZoneName;
        if (!zName) return;
        const key = normalizeKey(zName);
        const svcId = r.ServiceID;
        if (wanted && svcId != null && String(svcId) !== wanted) return;

        const isCompleted = (r.IsVerifyByHallManager === 1 || r.IsVerifyByHallManager === true) &&
                            (r.IsVerifyByOrganizer === 1 || r.IsVerifyByOrganizer === true);
        const stat = r.ZoneStatus || (isCompleted ? "Completed" : r.WorkStatusID === 2 ? "InProgress" : "Pending");
        if (!zoneTasks.has(key)) zoneTasks.set(key, []);
        zoneTasks.get(key).push(stat);
      });
    }

    const map = new Map();
    (zones || []).forEach((z) => {
      const k = normalizeKey(z.Zone || z.ZoneName);
      if (k) map.set(k, "Pending");
    });

    zoneTasks.forEach((stats, key) => {
      if (!stats || stats.length === 0) return;
      const allComp = stats.every((s) => s === "Completed");
      if (allComp) {
        map.set(key, "Completed");
      } else if (stats.every((s) => s === "Pending")) {
        map.set(key, "Pending");
      } else {
        map.set(key, "InProgress");
      }
    });

    return map;
  }, [isServicePartnerActive, isExhibitorOrdersActive, effectiveServicePartnerRows, effectiveExhibitorOrderRows, zones, selectedServiceId]);

  // 3. Status per Hall (for Hall Status View)
  const { hallStatusMap, hallDailyRoutineSummary } = useMemo(() => {
    const wanted = selectedServiceId ? String(selectedServiceId) : null;
    const hallStats = new Map();
    const dailySummary = new Map();

    if (isServicePartnerActive) {
      effectiveServicePartnerRows.forEach((r) => {
        const hName = r.HallName || r.Hall;
        if (!hName) return;
        const key = normalizeKey(hName);
        const svcId = firstValue(r.ServiceID);
        if (wanted && svcId != null && String(svcId) !== wanted) return;

        const stat = r.HallStatus || r.StallTaskStatus || "Pending";
        if (!hallStats.has(key)) hallStats.set(key, []);
        hallStats.get(key).push(stat);
      });
    }

    if (isExhibitorOrdersActive) {
      effectiveExhibitorOrderRows.forEach((r) => {
        const hName = r.Hall || r.HallName;
        if (!hName) return;
        const key = normalizeKey(hName);
        const svcId = r.ServiceID;
        if (wanted && svcId != null && String(svcId) !== wanted) return;

        const stat = r.HallStatus || (r.IsVerifyByOrganizer && r.IsVerifyByHallManager ? "Completed" : "Pending");
        if (!hallStats.has(key)) hallStats.set(key, []);
        hallStats.get(key).push(stat);
      });
    }

    if (isDailyRoutineActive) {
      effectiveDailyRoutineRows.forEach((r) => {
        const hName = r.HallName || r.Hall;
        if (!hName) return;
        const key = normalizeKey(hName);
        const stat = r.HallStatus || (r.TotalCompCount === r.TotalCount && r.TotalCount > 0 ? "Completed" : r.TotalInPCount > 0 ? "InProgress" : "Pending");
        if (!hallStats.has(key)) hallStats.set(key, []);
        hallStats.get(key).push(stat);

        if (!dailySummary.has(key)) {
          dailySummary.set(key, { total: 0, completed: 0, inProgress: 0, programs: [] });
        }
        const summ = dailySummary.get(key);
        summ.total += Number(r.TotalCount || 0);
        summ.completed += Number(r.TotalCompCount || 0);
        summ.inProgress += Number(r.TotalInPCount || 0);
        summ.programs.push({
          title: r.ProgramTitle,
          times: r.ScheduleTimes,
          responsible: r.ResponsiblePerson,
          status: stat,
        });
      });
    }

    const map = new Map();
    (halls || []).forEach((h) => {
      const k = normalizeKey(h.HallName);
      if (k) map.set(k, "Pending");
    });

    hallStats.forEach((stats, key) => {
      if (!stats || stats.length === 0) return;
      const allComp = stats.every((s) => s === "Completed");
      if (allComp) {
        map.set(key, "Completed");
      } else if (stats.every((s) => s === "Pending")) {
        map.set(key, "Pending");
      } else {
        map.set(key, "InProgress");
      }
    });

    return { hallStatusMap: map, hallDailyRoutineSummary: dailySummary };
  }, [isServicePartnerActive, isExhibitorOrdersActive, isDailyRoutineActive, effectiveServicePartnerRows, effectiveExhibitorOrderRows, effectiveDailyRoutineRows, halls, selectedServiceId]);

  // What a booth should read as right now. A stall without tasks in active filters is unallocated.
  const boothDisplayStatus = useCallback((booth) => {
    if (!booth) return { status: null, dimmed: false };
    const status = statusByBooth.get(normalizeBoothKey(booth.BoothName));
    return status ? { status, dimmed: false } : { status: null, dimmed: true };
  }, [statusByBooth]);

  const boothDisplayStatusRef = useRef(boothDisplayStatus);
  useEffect(() => {
    boothDisplayStatusRef.current = boothDisplayStatus;
  }, [boothDisplayStatus]);

  const serviceNameById = useMemo(() => {
    const map = new Map();
    (services || []).forEach((svc) => {
      if (svc && svc.ServiceID != null) {
        map.set(String(svc.ServiceID), svc.Name || svc.DisplayName);
      }
    });
    return map;
  }, [services]);

  const serviceNameOf = (row) =>
    row.name ||
    (row.ServiceID != null ? serviceNameById.get(String(row.ServiceID)) : null) ||
    null;

  // Stall codes belonging to the selected zone.
  const zoneBoothKeys = useMemo(() => {
    if (!selectedZoneId) return null;
    const set = new Set();

    (zoneStalls || []).forEach(s => {
      if (String(s.HallZoneMappingID) !== String(selectedZoneId)) return;
      if (s.StallName) set.add(normalizeBoothKey(s.StallName));
    });

    const picked = (zones || []).find(
      z => String(z.HallZoneMappingID) === String(selectedZoneId)
    );
    const wantedZone = String((picked && picked.Zone) || "").trim().toLowerCase();
    if (wantedZone) {
      effectiveServicePartnerRows.forEach(r => {
        const zone = String(firstValue(r.ZoneName) || firstValue(r.Zone) || "")
          .trim()
          .toLowerCase();
        if (!zone || zone !== wantedZone) return;
        if (r.StallName) set.add(normalizeBoothKey(r.StallName));
      });
      effectiveExhibitorOrderRows.forEach(r => {
        const zone = String(r.Zone || r.ZoneName || "").trim().toLowerCase();
        if (!zone || zone !== wantedZone) return;
        if (r.Stall || r.StallName) set.add(normalizeBoothKey(r.Stall || r.StallName));
      });
    }

    try {
      window.__zoneFocus = {
        selectedZoneId,
        zoneName: (picked && picked.Zone) || null,
        fromZoneApi: (zoneStalls || []).filter(
          z => String(z.HallZoneMappingID) === String(selectedZoneId)
        ).length,
        zoneApiRowsTotal: (zoneStalls || []).length,
        fromTracker: wantedZone
          ? effectiveServicePartnerRows.filter(r => {
            const z = String(firstValue(r.ZoneName) || firstValue(r.Zone) || "").trim().toLowerCase();
            return z && z === wantedZone;
          }).length
          : 0,
        trackerNamesZones: effectiveServicePartnerRows.some(
          r => firstValue(r.ZoneName) || firstValue(r.Zone)
        ),
        stalls: Array.from(set),
      };
    } catch (e) { }

    return set;
  }, [zoneStalls, selectedZoneId, zones, effectiveServicePartnerRows, effectiveExhibitorOrderRows]);

  const boothMap = useMemo(() => {
    const map = {};
    booths.forEach(b => { map[b.BoothName] = b; });
    return map;
  }, [booths]);

  // Dynamic legend counts according to viewFilter:
  const dynamicStatusCounts = useMemo(() => {
    const counts = { Completed: 0, InProgress: 0, Pending: 0, Delayed: 0 };
    if (currentViewFilter === "stall") {
      const seen = new Set();
      booths.forEach((b) => {
        const key = normalizeBoothKey(b.BoothName);
        if (seen.has(key)) return;
        seen.add(key);
        const st = statusByBooth.get(key);
        if (st && counts[st] != null) counts[st] += 1;
      });
    } else if (currentViewFilter === "zone") {
      const allZones = zones && zones.length > 0 ? zones : Array.from(zoneStatusMap.keys()).map(z => ({ Zone: z }));
      const seen = new Set();
      allZones.forEach((z) => {
        const k = normalizeKey(z.Zone || z.ZoneName);
        if (seen.has(k)) return;
        seen.add(k);
        const st = zoneStatusMap.get(k) || "Pending";
        if (counts[st] != null) counts[st] += 1;
      });
    } else if (currentViewFilter === "hall") {
      const allHalls = halls && halls.length > 0 ? halls : Array.from(hallStatusMap.keys()).map(h => ({ HallName: h }));
      const seen = new Set();
      allHalls.forEach((h) => {
        const k = normalizeKey(h.HallName);
        if (seen.has(k)) return;
        seen.add(k);
        const st = hallStatusMap.get(k) || "Pending";
        if (counts[st] != null) counts[st] += 1;
      });
    }
    return counts;
  }, [currentViewFilter, statusByBooth, zoneStatusMap, hallStatusMap, booths, zones, halls]);

  // Keep the plan covering the viewport: pin the offsets so an edge can never
  // be dragged inward to reveal background, and centre an axis that genuinely
  // is smaller than the box.
  const clampTransform = useCallback(() => {
    const container = containerRef.current;
    const svgElement = svgRef.current && svgRef.current.querySelector("svg");
    if (!container || !svgElement) return;

    const bounds = container.getBoundingClientRect();
    const rectW = bounds.width;
    const rectH = bounds.height;
    if (!rectW || !rectH) return;

    const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
    const vbW = (vb && vb.width) || 792;
    const vbH = (vb && vb.height) || 612;

    const pxPerUnit = Math.min(rectW / vbW, rectH / vbH);
    const padX = (rectW - vbW * pxPerUnit) / 2;
    const padY = (rectH - vbH * pxPerUnit) / 2;

    const { scale } = transform.current;
    const drawnW = vbW * pxPerUnit * scale;
    const drawnH = vbH * pxPerUnit * scale;

    const clampAxis = (offset, pad, drawn, viewport) => {
      const lead = pad * scale;
      if (drawn <= viewport) return (viewport - drawn) / 2 - lead;
      return Math.min(-lead, Math.max(viewport - drawn - lead, offset));
    };

    transform.current.offsetX = clampAxis(transform.current.offsetX, padX, drawnW, rectW);
    transform.current.offsetY = clampAxis(transform.current.offsetY, padY, drawnH, rectH);
  }, []);

  const updateTransformStyle = useCallback((animate = false) => {
    if (!svgRef.current) return;
    clampTransform();
    const { scale, offsetX, offsetY } = transform.current;

    svgRef.current.style.willChange = "transform";
    if (willChangeTimeoutRef.current) clearTimeout(willChangeTimeoutRef.current);
    willChangeTimeoutRef.current = setTimeout(() => {
      if (svgRef.current) svgRef.current.style.willChange = "auto";
    }, animate ? 400 : 200);

    if (animate) {
      svgRef.current.style.transition = "transform 0.35s ease-in-out";
      setTimeout(() => {
        if (svgRef.current) svgRef.current.style.transition = "none";
      }, 350);
    } else {
      svgRef.current.style.transition = "none";
    }

    svgRef.current.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${scale})`;

    if (zoomLabelRef.current) {
      zoomLabelRef.current.innerText = `${Math.round(scale * 100)}%`;
    }
  }, [clampTransform]);

  // Focus box for a booth code, in SVG root user space. Multiple occurrences of
  // one code are unioned, unless that union is implausibly large (the same code
  // printed in two unrelated places), in which case the first wins.
  // Unpadded label extent. This is what routing treats as the booth footprint.
  const getBoothRawBox = useCallback((boothName) => {
    // The drawn border when we found it - it is the stall, not an estimate of one.
    const outline = boothOutlinesRef.current.get(boothName);
    if (outline) return outline;

    const els = boothElementsRef.current.get(boothName) || [];
    if (els.length === 0) return null;
    const boxes = els.map(elementBoxInSvgSpace).filter(Boolean);
    if (boxes.length === 0) return null;

    const merged = unionBoxes(boxes);
    const svgElement = svgRef.current && svgRef.current.querySelector("svg");
    const vw = (svgElement && svgElement.viewBox && svgElement.viewBox.baseVal && svgElement.viewBox.baseVal.width) || 792;
    const vh = (svgElement && svgElement.viewBox && svgElement.viewBox.baseVal && svgElement.viewBox.baseVal.height) || 612;
    if (merged && (merged.width > vw * 0.25 || merged.height > vh * 0.25)) {
      return boxes[0];
    }
    return merged;
  }, []);

  // Box for the focus ring and hit target. A real border is used as drawn; only
  // the label-derived fallback is padded out to stall size.
  // The traced outline of one or more booths as a single SVG path. Unioning is
  // just concatenating the cell sets before tracing - the shared edge between
  // two touching stalls appears twice and cancels, so a zone comes back as one
  // region with its own outer boundary rather than as a pile of rectangles.
  const getShapePath = useCallback((boothNames, club) => {
    const store = boothOutlinesRef.current;
    if (!store || !store.cells || !store.grid) return null;
    const grid = store.grid;

    // A booth without a traced border - the fallback padded label, or one
    // rebuilt from its printed size - has no entry in store.cells at all. Left
    // out here, it left a literal hole in the middle of the zone: the shape
    // only ever covered the booths that happened to trace cleanly. Rasterising
    // its own box onto the same grid gives it cells too, so every selected
    // booth is represented before clubbing runs.
    const boxToCells = (box) => {
      if (!box) return [];
      const c0 = Math.max(0, Math.floor((box.x - grid.ox) / grid.cell));
      const r0 = Math.max(0, Math.floor((box.y - grid.oy) / grid.cell));
      const c1 = Math.min(grid.cols - 1, Math.ceil((box.x + box.width - grid.ox) / grid.cell) - 1);
      const r1 = Math.min(grid.rows - 1, Math.ceil((box.y + box.height - grid.oy) / grid.cell) - 1);
      const cells = [];
      for (let r = r0; r <= r1; r += 1) {
        for (let c = c0; c <= c1; c += 1) cells.push(r * grid.cols + c);
      }
      return cells;
    };

    const all = [];
    boothNames.forEach((name) => {
      const cells = store.cells.get(name);
      if (cells && cells.length > 0) { all.push(...cells); return; }
      all.push(...boxToCells(getBoothBox(name)));
    });
    if (all.length === 0) return null;
    let cells = Array.from(new Set(all));
    // A zone is one thing, so it is drawn as one thing: the stalls are closed
    // together across the aisles between them and traced as a single boundary,
    // instead of as a scatter of separate outlines.
    if (club && cells.length > 0) cells = clubCells(cells, grid, ZONE_CLUB_MAX_RADIUS);
    return cellsToPath(cells, grid);
    // getBoothBox is declared just below and is itself referentially stable
    // (empty dep array) - reordering the two would only churn this callback's
    // identity for no behavioural change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getBoothBox = useCallback((boothName) => {
    if (boothOutlinesRef.current.has(boothName)) return getBoothRawBox(boothName);
    return padBox(getBoothRawBox(boothName));
  }, [getBoothRawBox]);

  // Projects a point from SVG user space into "world" space: container pixels
  // as they would be at scale 1 / zero offset. getScreenCTM() already folds in
  // the wrapper's CSS transform, so we divide it back out.
  const svgPointToWorld = useCallback((x, y, ctm, containerRect) => {
    const { scale, offsetX, offsetY } = transform.current;
    const sx = ctm ? ctm.a * x + ctm.c * y + ctm.e : x;
    const sy = ctm ? ctm.b * x + ctm.d * y + ctm.f : y;
    return {
      x: (sx - containerRect.left - offsetX) / scale,
      y: (sy - containerRect.top - offsetY) / scale,
    };
  }, []);

  // Frames an SVG-user-space box in the viewport.
  const zoomToSvgBox = useCallback((box) => {
    if (!box || !svgRef.current || !containerRef.current) return false;
    const svgElement = svgRef.current.querySelector("svg");
    if (!svgElement) return false;

    const containerRect = containerRef.current.getBoundingClientRect();
    if (!containerRect.width || !containerRect.height) return false;

    const ctm = typeof svgElement.getScreenCTM === "function" ? svgElement.getScreenCTM() : null;
    const a = svgPointToWorld(box.x, box.y, ctm, containerRect);
    const b = svgPointToWorld(box.x + box.width, box.y + box.height, ctm, containerRect);

    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);
    const minY = Math.min(a.y, b.y);
    const maxY = Math.max(a.y, b.y);

    const width = Math.max(maxX - minX, 1);
    const height = Math.max(maxY - minY, 1);

    const padding = 0.12;
    // No cover floor here: a hall is tall and narrow, and forcing it up to the
    // fit-width scale would frame only part of it. clampTransform still stops
    // the view drifting off the plan.
    const newScale = Math.max(0.4, Math.min(8, Math.min(
      (containerRect.width * (1 - padding * 2)) / width,
      (containerRect.height * (1 - padding * 2)) / height
    )));

    transform.current.scale = newScale;
    transform.current.offsetX = containerRect.width / 2 - ((minX + maxX) / 2) * newScale;
    transform.current.offsetY = containerRect.height / 2 - ((minY + maxY) / 2) * newScale;

    updateTransformStyle(true);
    return true;
  }, [svgPointToWorld, updateTransformStyle]);

  // Spotlight overlay. Takes a LIST of boxes and punches one hole per booth, so
  // a zone lights up only its own stalls - a single hole over the zone's bounding
  // box would leave every unrelated booth inside that rectangle lit too.
  const drawFocusOverlay = useCallback((boxes, label, shapePath) => {
    const svgElement = svgRef.current && svgRef.current.querySelector("svg");
    if (!svgElement) return;

    let g = svgElement.querySelector("g#booth-focus");
    if (!g) {
      g = document.createElementNS("http://www.w3.org/2000/svg", "g");
      g.setAttribute("id", "booth-focus");
      g.setAttribute("pointer-events", "none");
    }
    // Always re-append so the overlay stays on top of later-added groups.
    svgElement.appendChild(g);
    g.innerHTML = "";

    const list = (Array.isArray(boxes) ? boxes : [boxes]).filter(Boolean);
    if (list.length === 0) return;

    const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
    const vx = (vb && vb.x) || 0;
    const vy = (vb && vb.y) || 0;
    const vw = (vb && vb.width) || 792;
    const vh = (vb && vb.height) || 612;

    const svgNs = "http://www.w3.org/2000/svg";
    const maskId = "booth-focus-mask";

    const mask = document.createElementNS(svgNs, "mask");
    mask.setAttribute("id", maskId);
    const maskShow = document.createElementNS(svgNs, "rect");
    maskShow.setAttribute("x", vx); maskShow.setAttribute("y", vy);
    maskShow.setAttribute("width", vw); maskShow.setAttribute("height", vh);
    maskShow.setAttribute("fill", "#fff");
    mask.appendChild(maskShow);
    // One path covering every selected stall when the true shapes are known, so
    // a five-sided stall is lit as itself and a zone reads as a single region.
    if (shapePath) {
      const hole = document.createElementNS(svgNs, "path");
      hole.setAttribute("d", shapePath);
      hole.setAttribute("fill", "#000");
      hole.setAttribute("fill-rule", "evenodd");
      mask.appendChild(hole);
    } else {
      list.forEach((box) => {
        const hole = document.createElementNS(svgNs, "rect");
        hole.setAttribute("x", box.x); hole.setAttribute("y", box.y);
        hole.setAttribute("width", box.width); hole.setAttribute("height", box.height);
        hole.setAttribute("fill", "#000");
        mask.appendChild(hole);
      });
    }
    g.appendChild(mask);

    const dim = document.createElementNS(svgNs, "rect");
    dim.setAttribute("x", vx); dim.setAttribute("y", vy);
    dim.setAttribute("width", vw); dim.setAttribute("height", vh);
    dim.setAttribute("fill", "#0f172a");
    dim.setAttribute("opacity", "0.55");
    dim.setAttribute("mask", `url(#${maskId})`);
    g.appendChild(dim);

    // Pulse a lone booth; a pulsing ring on 40 stalls is just noise.
    const single = list.length === 1;
    if (shapePath) {
      const ring = document.createElementNS(svgNs, "path");
      ring.setAttribute("d", shapePath);
      ring.setAttribute("fill", "none");
      ring.setAttribute("fill-rule", "evenodd");
      ring.setAttribute("stroke", "#4f46e5");
      ring.setAttribute("stroke-width", single ? "1.5" : "1.1");
      ring.setAttribute("stroke-linejoin", "round");
      if (single) ring.setAttribute("class", "booth-focus-ring");
      g.appendChild(ring);
    } else {
      list.forEach((box) => {
        const ring = document.createElementNS(svgNs, "rect");
        ring.setAttribute("x", box.x); ring.setAttribute("y", box.y);
        ring.setAttribute("width", box.width); ring.setAttribute("height", box.height);
        ring.setAttribute("fill", "none");
        ring.setAttribute("stroke", "#4f46e5");
        ring.setAttribute("stroke-width", single ? "1.5" : "0.9");
        if (single) ring.setAttribute("class", "booth-focus-ring");
        g.appendChild(ring);
      });
    }

    if (label) {
      const union = unionBoxes(list);
      const text = document.createElementNS(svgNs, "text");
      text.setAttribute("x", union.x + union.width / 2);
      text.setAttribute("y", union.y - 3);
      text.setAttribute("text-anchor", "middle");
      text.setAttribute("font-size", "7");
      text.setAttribute("font-weight", "bold");
      text.setAttribute("fill", "#4f46e5");
      text.setAttribute("data-overlay", "1");
      text.textContent = label;
      g.appendChild(text);
    }
  }, []);

  // Opening view: the plan's full width, so every hall is visible left to
  // right. It overflows vertically at that scale, which the user pans through.
  const applyDefaultView = useCallback((animate = false) => {
    if (!svgRef.current || !containerRef.current) return false;
    const svgElement = svgRef.current.querySelector("svg");
    if (!svgElement) return false;

    const rect = containerRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return false;

    const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
    const vbX = (vb && vb.x) || 0;
    const vbY = (vb && vb.y) || 0;
    const vbW = (vb && vb.width) || 792;
    const vbH = (vb && vb.height) || 612;

    // preserveAspectRatio="xMidYMid meet": uniform scale, centred, so this is
    // the px-per-unit and padding the SVG viewport itself applies at scale 1.
    const pxPerUnit = Math.min(rect.width / vbW, rect.height / vbH);
    const padX = (rect.width - vbW * pxPerUnit) / 2;
    const padY = (rect.height - vbH * pxPerUnit) / 2;

    // Fit the plan's full width, so every hall is visible left to right. The
    // drawing is taller than the box at this scale, which is intended - the
    // overflow is panned vertically.
    const scale = Math.max(1, rect.width / (vbW * pxPerUnit));

    // Keep the plan's centre in the middle of the viewport.
    const centreBaseX = (vbX + vbW / 2 - vbX) * pxPerUnit + padX;
    const centreBaseY = (vbY + vbH / 2 - vbY) * pxPerUnit + padY;

    transform.current.scale = scale;
    transform.current.offsetX = rect.width / 2 - centreBaseX * scale;
    transform.current.offsetY = rect.height / 2 - centreBaseY * scale;
    updateTransformStyle(animate);
    return true;
  }, [updateTransformStyle]);

  // Keep the canvas box locked to the drawing's aspect ratio, capped so a tall
  // plan can't push the rest of the page off screen.
  useEffect(() => {
    const wrap = canvasWrapRef.current;
    if (!wrap) return;

    const measure = () => {
      const available = wrap.clientWidth;
      if (!available) return;
      // Full width always. Height is capped at the plan's own height once its
      // width fits, so a narrow screen can never letterbox top and bottom.
      const aspect = svgAspect || 792 / 612;
      const height = Math.max(
        300,
        Math.min(Math.round(window.innerHeight * 0.72), Math.round(available / aspect))
      );
      setCanvasBox((prev) => (prev.width === available && prev.height === height ? prev : { width: available, height }));
    };

    measure();

    if (typeof window.ResizeObserver !== "function") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new window.ResizeObserver(measure);
    ro.observe(wrap);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [svgAspect]);

  // Apply the opening view once the drawing has been measured, and again if the
  // box is resized - but never after the user has panned or zoomed themselves.
  useEffect(() => {
    if (hasUserAdjustedRef.current) return;
    if (filterHallId || filterBoothId) return;
    if (booths.length === 0) return;

    const timer = setTimeout(() => {
      if (hasUserAdjustedRef.current) return;
      if (applyDefaultView(defaultViewAppliedRef.current)) {
        defaultViewAppliedRef.current = true;
      }
    }, 40);
    return () => clearTimeout(timer);
  }, [booths, canvasBox, filterHallId, filterBoothId, applyDefaultView]);

  // A new drawing gets a fresh opening view.
  useEffect(() => {
    hasUserAdjustedRef.current = false;
    defaultViewAppliedRef.current = false;
  }, [layout.svgContent]);

  useEffect(() => {
    updateTransformStyle();
  }, [updateTransformStyle]);

  const zoomAtPoint = useCallback((newScale, pointX, pointY) => {
    const clampedScale = Math.max(0.4, Math.min(8, newScale));
    const { scale, offsetX, offsetY } = transform.current;
    if (clampedScale === scale) return;

    const worldX = (pointX - offsetX) / scale;
    const worldY = (pointY - offsetY) / scale;

    transform.current.scale = clampedScale;
    transform.current.offsetX = pointX - worldX * clampedScale;
    transform.current.offsetY = pointY - worldY * clampedScale;
  }, []);


  // ─── Build index, paint status colours, wire interaction ──────────────
  // Keyed to the SVG itself: a filter change must never rebuild the graph.
  // ─── Read halls & booths out of the drawing ───────────────────────────
  // Runs once per SVG. Everything downstream keys off what this finds.
  useEffect(() => {
    if (!svgRef.current || !layout.svgContent) return;

    const svgContainer = svgRef.current;
    const timer = setTimeout(() => {
      const svgElement = svgContainer.querySelector('svg');
      if (!svgElement) return;

      clipIndexRef.current = buildSvgIndex(svgElement);

      // The exported SVG carries width="792" height="612", so it renders at its
      // intrinsic size and sits marooned in a wider container. Let it scale to
      // whatever box we give it instead. All our geometry goes through
      // getScreenCTM / getCTM, so this does not disturb focus or routing.
      svgElement.setAttribute("width", "100%");
      svgElement.setAttribute("height", "100%");
      const vbox = svgElement.viewBox && svgElement.viewBox.baseVal;
      if (vbox && vbox.width > 0 && vbox.height > 0) {
        setSvgAspect(vbox.width / vbox.height);
      }
      if (!svgElement.getAttribute("preserveAspectRatio")) {
        svgElement.setAttribute("preserveAspectRatio", "xMidYMid meet");
      }

      const { halls: svgHalls, booths: svgBooths, elementsByBooth } = extractSvgLayoutData(svgElement);
      boothElementsRef.current = elementsByBooth;
      boothOutlinesRef.current = findBoothOutlines(svgElement, elementsByBooth);
      setSvgData({ halls: svgHalls, booths: svgBooths });
      if (onSvgDataExtracted) onSvgDataExtracted({ halls: svgHalls, booths: svgBooths });
    }, 60);

    return () => clearTimeout(timer);
    // onSvgDataExtracted intentionally omitted: parents commonly pass an inline
    // callback, which would re-run the parse on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout.svgContent]);

  useEffect(() => {
    if (!svgRef.current || !layout.svgContent) return;

    const svgContainer = svgRef.current;
    let cleanupListeners = null;

    const timer = setTimeout(() => {
      const svgElement = svgContainer.querySelector('svg');
      if (!svgElement) return;

      // Color injection (fast)
      let styleTag = svgElement.querySelector('style[data-booth-colors]');
      if (!styleTag) {
        styleTag = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        styleTag.setAttribute('data-booth-colors', 'true');
        styleTag.setAttribute('type', 'text/css');
        svgElement.insertBefore(styleTag, svgElement.firstChild);
      }

      let cssRules = '';
      // Booth colours are no longer painted from getHallLayout's StatusColor.
      // Status comes from the tracker now, and it is carried by the node dots -
      // painting a second, differently-sourced colour underneath them meant the
      // drawing could contradict the popup.

      // Booth label nodes are fill-opacity:0, so they are never "painted" and
      // the default visiblePainted hit-testing ignores them entirely.
      cssRules += `
        [data-booth-name] { pointer-events: all; cursor: pointer; }
        #booth-hits [data-booth-name] { fill: transparent; pointer-events: all; cursor: pointer; }
        #booth-nodes circle.booth-node-dot { cursor: pointer; transition: r 0.15s ease, filter 0.15s ease; }
        #booth-nodes circle.booth-node-dot:hover { r: 4.5; filter: drop-shadow(0 0 3px rgba(79,70,229,0.9)); }
        #booth-areas .booth-area { transition: fill-opacity 0.15s ease, stroke-width 0.15s ease; stroke-linejoin: round; vector-effect: non-scaling-stroke; }
        #booth-areas .booth-area:hover { fill-opacity: 0.4 !important; stroke-width: 2.2 !important; }
        #booth-focus .booth-focus-ring { animation: boothFocusPulse 1.6s ease-in-out infinite; }
        @keyframes boothFocusPulse {
          0%, 100% { stroke-opacity: 1; stroke-width: 1.8; }
          50%      { stroke-opacity: .45; stroke-width: 2.8; }
        }
      `;
      styleTag.textContent = cssRules;

      // ─── Booth nodes from the label geometry ───
      const nodes = {};
      const nodeBoothMap = {};
      const boothBoxes = {};
      booths.forEach(booth => {
        const raw = getBoothRawBox(booth.BoothName);
        if (!raw) return;
        const box = boothOutlinesRef.current.has(booth.BoothName) ? raw : padBox(raw);
        const id = booth.BoothName;
        nodes[id] = { id, x: box.x + box.width / 2, y: box.y + box.height / 2 };
        nodeBoothMap[id] = booth;
        boothBoxes[id] = box;
      });

      boothNodesRef.current = nodes;
      nodeBoothMapRef.current = nodeBoothMap;

      /* The stall itself carries the status. Appended before the hit targets and
         the dots so it sits underneath them, and underneath the drawing's own
         booth number and dimension text */
      let areasGroup = svgElement.querySelector('g#booth-areas');
      if (!areasGroup) {
        areasGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        areasGroup.setAttribute('id', 'booth-areas');
        svgElement.appendChild(areasGroup);
      }
      areasGroup.innerHTML = '';
      // A combined stand carries several booth codes inside one border, so the
      // same box/path comes back for each of them. The fill is semi-transparent, and
      // painting it once per code stacks the opacity until the stand reads far
      // darker than a single stall - so each distinct area is painted once.
      const paintedAreas = new Set();
      Object.entries(boothBoxes).forEach(([id, box]) => {
        const areaKey = `${Math.round(box.x * 2) / 2}:${Math.round(box.y * 2) / 2}:${Math.round(box.width * 2) / 2}:${Math.round(box.height * 2) / 2}`;
        if (paintedAreas.has(areaKey)) return;
        paintedAreas.add(areaKey);

        const area = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        area.setAttribute('x', box.x);
        area.setAttribute('y', box.y);
        area.setAttribute('width', box.width);
        area.setAttribute('height', box.height);
        area.setAttribute('class', 'booth-area');
        area.setAttribute('data-node-id', id);
        const st = boothDisplayStatusRef.current(nodeBoothMap[id]);
        paintBoothArea(area, st.status, st.dimmed);
        areasGroup.appendChild(area);
      });

      // Generous transparent hit targets - the label glyphs alone are tiny.
      let hitsGroup = svgElement.querySelector('g#booth-hits');
      if (!hitsGroup) {
        hitsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        hitsGroup.setAttribute('id', 'booth-hits');
        svgElement.appendChild(hitsGroup);
      }
      hitsGroup.innerHTML = '';
      Object.entries(boothBoxes).forEach(([id, box]) => {
        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', box.x);
        rect.setAttribute('y', box.y);
        rect.setAttribute('width', box.width);
        rect.setAttribute('height', box.height);
        rect.setAttribute('fill', 'transparent');
        rect.setAttribute('data-booth-name', id);
        hitsGroup.appendChild(rect);
      });

      // Draw dots
      let dotsGroup = svgElement.querySelector('g#booth-nodes');
      if (!dotsGroup) {
        dotsGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        dotsGroup.setAttribute('id', 'booth-nodes');
        svgElement.appendChild(dotsGroup);
      }
      dotsGroup.innerHTML = '';

      Object.values(nodes).forEach(node => {
        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', node.x);
        circle.setAttribute('cy', node.y);
        circle.setAttribute('r', '2.5');
        // The drawing has no fillable booth shape, so the dot carries status.
        // Painted with the booth's own status here; the service-filter effect
        // below repaints once the dots exist, and owns it from then on.
        {
          // Same source as the popup, so a dot can never disagree with the
          // status shown when the booth is opened.
          const st = boothDisplayStatusRef.current(nodeBoothMap[node.id]);
          paintBoothDot(circle, st.status, st.dimmed);
        }
        circle.setAttribute('stroke', '#fff');
        circle.setAttribute('stroke-width', '1');
        circle.setAttribute('class', 'booth-node-dot');
        /* The stall carries the colour now, so the dot is no longer drawn. It
           stays in the DOM because the route finder and the service filter both
           address it by node id */
        circle.style.display = 'none';
        circle.setAttribute('data-node-id', node.id);
        circle.style.pointerEvents = 'all';

        // Node click -> open popup with that node's booth data
        circle.addEventListener('click', (e) => {
          e.stopPropagation();
          if (transform.current.hasDragged) return;
          const booth = nodeBoothMap[node.id];
          if (booth) setSelectedBooth(booth);
        });

        // Node hover -> reuse existing tooltip
        circle.addEventListener('mouseover', () => {
          const booth = nodeBoothMap[node.id];
          if (booth && tooltipRef.current && tooltipContentRef.current && tooltipDotRef.current && svgContainer) {
            const rect = circle.getBoundingClientRect();
            const { status, dimmed } = boothDisplayStatusRef.current(booth);
            tooltipContentRef.current.innerText =
              `${booth.BoothName} - ${dimmed ? 'Not in this service' : status || 'Unknown'}`;
            if (status) {
              tooltipDotRef.current.style.display = 'inline-block';
              tooltipDotRef.current.style.backgroundColor = getStatusColor(status);
            } else {
              tooltipDotRef.current.style.display = 'none';
            }
            tooltipRef.current.style.left = `${rect.left}px`;
            tooltipRef.current.style.top = `${rect.top - 10}px`;
            tooltipRef.current.style.display = 'block';
          }
        });
        circle.addEventListener('mouseout', () => {
          if (tooltipRef.current) tooltipRef.current.style.display = 'none';
        });

        dotsGroup.appendChild(circle);

        /* With the dot hidden the stall is what the pointer meets, so it takes
           the click and hover the dot used to carry */
        const area = areasGroup.querySelector('[data-node-id="' + node.id.replace(/"/g, '\\"') + '"]');
        if (area) {
          area.style.cursor = 'pointer';
          area.style.pointerEvents = 'all';
          area.addEventListener('click', (e) => {
            e.stopPropagation();
            if (transform.current.hasDragged) return;
            const booth = nodeBoothMap[node.id];
            if (booth) setSelectedBooth(booth);
          });
          area.addEventListener('mouseover', () => {
            const booth = nodeBoothMap[node.id];
            if (booth && tooltipRef.current && tooltipContentRef.current && tooltipDotRef.current && svgContainer) {
              const rect = area.getBoundingClientRect();
              const { status, dimmed } = boothDisplayStatusRef.current(booth);
              tooltipContentRef.current.innerText =
                booth.BoothName + ' - ' + (dimmed ? 'Not in this service' : status || 'Unknown');
              if (status) {
                tooltipDotRef.current.style.display = 'inline-block';
                tooltipDotRef.current.style.backgroundColor = getStatusColor(status);
              } else {
                tooltipDotRef.current.style.display = 'none';
              }
              tooltipRef.current.style.left = rect.left + 'px';
              tooltipRef.current.style.top = (rect.top - 10) + 'px';
              tooltipRef.current.style.display = 'block';
            }
          });
          area.addEventListener('mouseout', () => {
            if (tooltipRef.current) tooltipRef.current.style.display = 'none';
          });
        }
      });
      setDotsVersion(v => v + 1);

      // Rasterise the hall once per layout. Booth footprints become walls; what
      // survives is the aisle network every route is confined to.
      const vb = svgElement.viewBox && svgElement.viewBox.baseVal;
      const viewBox = {
        x: (vb && vb.x) || 0,
        y: (vb && vb.y) || 0,
        width: (vb && vb.width) || 792,
        height: (vb && vb.height) || 612,
      };
      navGridRef.current = buildNavGrid(svgElement, viewBox);

      let routeGroup = svgElement.querySelector('g#route-path');
      if (!routeGroup) {
        routeGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        routeGroup.setAttribute('id', 'route-path');
        svgElement.appendChild(routeGroup);
      }

      // Event Delegation for hover
      const handleMouseOver = (e) => {
        const target = e.target.closest('[data-booth-name]');
        if (target && tooltipRef.current && tooltipContentRef.current && tooltipDotRef.current) {
          const boothName = target.getAttribute('data-booth-name');
          if (boothName && boothMap[boothName]) {
            const booth = boothMap[boothName];
            const rect = target.getBoundingClientRect();

            const { status, dimmed } = boothDisplayStatusRef.current(booth);
            tooltipContentRef.current.innerText =
              `${booth.BoothName} - ${dimmed ? 'Not in this service' : status || 'Unknown'}`;

            if (status) {
              tooltipDotRef.current.style.display = 'inline-block';
              tooltipDotRef.current.style.backgroundColor = getStatusColor(status);
            } else {
              tooltipDotRef.current.style.display = 'none';
            }

            tooltipRef.current.style.left = `${rect.left}px`;
            tooltipRef.current.style.top = `${rect.top - 10}px`;
            tooltipRef.current.style.display = 'block';
          }
        }
      };

      const handleMouseOut = (e) => {
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }
      };

      const handleClick = (e) => {
        if (transform.current.hasDragged) {
          console.log("Click ignored due to dragging");
          return;
        }
        const target = e.target.closest('[data-booth-name]');
        console.log("SVG Clicked! Target found:", target ? target.getAttribute('data-booth-name') : "None");
        if (target) {
          const boothName = target.getAttribute('data-booth-name');
          if (boothName && boothMap[boothName]) {
            console.log("Setting selected booth:", boothMap[boothName]);
            setSelectedBooth(boothMap[boothName]);
          }
        } else {
          setSelectedBooth(null);
        }
      };

      let cursorStyleTag = svgElement.querySelector('style[data-booth-cursor]');
      if (!cursorStyleTag) {
        cursorStyleTag = document.createElementNS('http://www.w3.org/2000/svg', 'style');
        cursorStyleTag.setAttribute('data-booth-cursor', 'true');
        cursorStyleTag.setAttribute('type', 'text/css');
        cursorStyleTag.textContent = `[data-booth-name] { cursor: pointer; }`;
        svgElement.insertBefore(cursorStyleTag, svgElement.firstChild);
      }

      svgContainer.addEventListener('mouseover', handleMouseOver);
      svgContainer.addEventListener('mouseout', handleMouseOut);
      svgContainer.addEventListener('click', handleClick);

      cleanupListeners = () => {
        svgContainer.removeEventListener('mouseover', handleMouseOver);
        svgContainer.removeEventListener('mouseout', handleMouseOut);
        svgContainer.removeEventListener('click', handleClick);
      };
    }, 100);

    return () => {
      clearTimeout(timer);
      // Returning this from inside the setTimeout would discard it and leak a
      // fresh set of listeners on every re-run.
      if (cleanupListeners) cleanupListeners();
    };
  }, [layout.svgContent, layout.booths, booths, boothMap, getBoothRawBox]);

  // ─── Multi-Mode Layer Rendering (Stall View / Zone Status / Hall Status) ──
  useEffect(() => {
    if (!svgRef.current) return;
    const svgElement = svgRef.current.querySelector('svg');
    if (!svgElement) return;

    const nodeBoothMap = nodeBoothMapRef.current || {};

    // 1. Group containers for zone and hall status overlays
    let zoneLayer = svgElement.querySelector('g#zone-status-layer');
    if (!zoneLayer) {
      zoneLayer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      zoneLayer.setAttribute('id', 'zone-status-layer');
      zoneLayer.setAttribute('pointer-events', 'all');
      svgElement.appendChild(zoneLayer);
    }

    let hallLayer = svgElement.querySelector('g#hall-status-layer');
    if (!hallLayer) {
      hallLayer = document.createElementNS('http://www.w3.org/2000/svg', 'g');
      hallLayer.setAttribute('id', 'hall-status-layer');
      hallLayer.setAttribute('pointer-events', 'all');
      svgElement.appendChild(hallLayer);
    }

    const areas = svgElement.querySelectorAll('#booth-areas .booth-area');
    const hits = svgElement.querySelectorAll('#booth-hits [data-booth-name]');
    const dots = svgElement.querySelectorAll('#booth-nodes circle.booth-node-dot');

    if (currentViewFilter === "stall") {
      // Show stall status fills & hit targets
      Array.from(areas).forEach((area) => {
        area.style.display = "";
        const booth = nodeBoothMap[area.getAttribute('data-node-id')];
        const { status, dimmed } = boothDisplayStatus(booth);
        paintBoothArea(area, status, dimmed);
      });
      Array.from(hits).forEach((hit) => {
        hit.style.display = "";
      });
      Array.from(dots).forEach((circle) => {
        circle.style.display = "none";
        const booth = nodeBoothMap[circle.getAttribute('data-node-id')];
        const { status, dimmed } = boothDisplayStatus(booth);
        paintBoothDot(circle, status, dimmed);
      });

      zoneLayer.style.display = "none";
      zoneLayer.innerHTML = "";
      hallLayer.style.display = "none";
      hallLayer.innerHTML = "";
    } else if (currentViewFilter === "zone") {
      // Hide individual stalls in Zone view
      Array.from(areas).forEach((area) => { area.style.display = "none"; });
      Array.from(hits).forEach((hit) => { hit.style.display = "none"; });
      Array.from(dots).forEach((circle) => { circle.style.display = "none"; });

      hallLayer.style.display = "none";
      hallLayer.innerHTML = "";
      zoneLayer.style.display = "";
      zoneLayer.innerHTML = "";

      // Render zones for all halls or current filtered hall
      const zonesToRender = (zones && zones.length > 0)
        ? zones
        : Array.from(zoneStatusMap.keys()).map(z => ({ Zone: z, HallZoneMappingID: z }));

      zonesToRender.forEach((zone) => {
        const zoneNameStr = zone.Zone || zone.ZoneName || "";
        if (!zoneNameStr) return;
        const zoneKey = normalizeKey(zoneNameStr);
        const zStat = zoneStatusMap.get(zoneKey) || "Pending";
        const color = getStatusColor(zStat);

        // Find booths belonging to this zone
        const stallKeys = new Set();
        (zoneStalls || []).forEach((s) => {
          if (String(s.HallZoneMappingID) === String(zone.HallZoneMappingID)) {
            stallKeys.add(normalizeBoothKey(s.StallName));
          }
        });
        effectiveServicePartnerRows.forEach((r) => {
          const z = normalizeKey(firstValue(r.ZoneName) || firstValue(r.Zone));
          if (z && z === zoneKey && r.StallName) stallKeys.add(normalizeBoothKey(r.StallName));
        });
        effectiveExhibitorOrderRows.forEach((r) => {
          const z = normalizeKey(r.Zone || r.ZoneName);
          if (z && z === zoneKey && (r.Stall || r.StallName)) stallKeys.add(normalizeBoothKey(r.Stall || r.StallName));
        });

        let zoneBooths = booths.filter((b) => stallKeys.has(normalizeBoothKey(b.BoothName)));
        if (filterHallId) {
          zoneBooths = zoneBooths.filter((b) => b.HallID === filterHallId);
        }

        if (zoneBooths.length > 0) {
          const shapePath = getShapePath(zoneBooths.map((b) => b.BoothName), true);
          if (shapePath) {
            const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            pathEl.setAttribute('d', shapePath);
            pathEl.setAttribute('fill', color);
            pathEl.setAttribute('fill-opacity', '0.38');
            pathEl.setAttribute('stroke', color);
            pathEl.setAttribute('stroke-width', '1.8');
            pathEl.setAttribute('class', 'zone-status-overlay');
            pathEl.style.cursor = 'pointer';
            pathEl.addEventListener('click', (e) => {
              e.stopPropagation();
              if (onZoneChange && zone.HallZoneMappingID) onZoneChange(zone.HallZoneMappingID);
            });
            pathEl.addEventListener('mouseover', (e) => {
              pathEl.setAttribute('fill-opacity', '0.55');
              if (tooltipRef.current && tooltipContentRef.current && tooltipDotRef.current) {
                const rect = pathEl.getBoundingClientRect();
                tooltipContentRef.current.innerText = `${zoneNameStr} - Status: ${zStat} (${zoneBooths.length} stalls)`;
                tooltipDotRef.current.style.display = 'inline-block';
                tooltipDotRef.current.style.backgroundColor = color;
                tooltipRef.current.style.left = `${rect.left + rect.width / 2}px`;
                tooltipRef.current.style.top = `${rect.top - 10}px`;
                tooltipRef.current.style.display = 'block';
              }
            });
            pathEl.addEventListener('mouseout', () => {
              pathEl.setAttribute('fill-opacity', '0.38');
              if (tooltipRef.current) tooltipRef.current.style.display = 'none';
            });
            zoneLayer.appendChild(pathEl);
          }

          // Floating Badge at zone center
          let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
          zoneBooths.forEach((b) => {
            const box = getBoothBox(b.BoothName);
            if (!box) return;
            minX = Math.min(minX, box.x);
            maxX = Math.max(maxX, box.x + box.width);
            minY = Math.min(minY, box.y);
            maxY = Math.max(maxY, box.y + box.height);
          });

          if (minX !== Infinity && maxX > minX) {
            const cx = (minX + maxX) / 2;
            const cy = (minY + maxY) / 2;
            const badgeGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            badgeGroup.setAttribute('class', 'zone-badge');
            badgeGroup.setAttribute('pointer-events', 'none');

            const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            const labelText = `${zoneNameStr} · ${zStat}`;
            const badgeW = Math.max(70, labelText.length * 6.5 + 16);
            bg.setAttribute('x', cx - badgeW / 2);
            bg.setAttribute('y', cy - 11);
            bg.setAttribute('width', badgeW);
            bg.setAttribute('height', '22');
            bg.setAttribute('rx', '4');
            bg.setAttribute('fill', '#ffffff');
            bg.setAttribute('fill-opacity', '0.94');
            bg.setAttribute('stroke', color);
            bg.setAttribute('stroke-width', '1.5');
            badgeGroup.appendChild(bg);

            const txt = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            txt.setAttribute('x', cx);
            txt.setAttribute('y', cy + 4);
            txt.setAttribute('text-anchor', 'middle');
            txt.setAttribute('font-size', '9.5');
            txt.setAttribute('font-weight', 'bold');
            txt.setAttribute('fill', '#1f2937');
            txt.textContent = labelText;
            badgeGroup.appendChild(txt);

            zoneLayer.appendChild(badgeGroup);
          }
        }
      });
    } else if (currentViewFilter === "hall") {
      // Hide individual stalls in Hall view
      Array.from(areas).forEach((area) => { area.style.display = "none"; });
      Array.from(hits).forEach((hit) => { hit.style.display = "none"; });
      Array.from(dots).forEach((circle) => { circle.style.display = "none"; });

      zoneLayer.style.display = "none";
      zoneLayer.innerHTML = "";
      hallLayer.style.display = "";
      hallLayer.innerHTML = "";

      const hallsToRender = halls && halls.length > 0 ? halls : [];

      hallsToRender.forEach((hall) => {
        const hKey = normalizeKey(hall.HallName);
        const hStat = hallStatusMap.get(hKey) || "Pending";
        const color = getStatusColor(hStat);
        const hallBooths = booths.filter((b) => b.HallID === hall.HallID);

        if (hallBooths.length > 0) {
          const shapePath = getShapePath(hallBooths.map((b) => b.BoothName), true);
          if (shapePath) {
            const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            pathEl.setAttribute('d', shapePath);
            pathEl.setAttribute('fill', color);
            pathEl.setAttribute('fill-opacity', '0.35');
            pathEl.setAttribute('stroke', color);
            pathEl.setAttribute('stroke-width', '2.5');
            pathEl.setAttribute('class', 'hall-status-overlay');
            pathEl.style.cursor = 'pointer';
            pathEl.addEventListener('click', (e) => {
              e.stopPropagation();
              if (onFilterHallChange) onFilterHallChange(hall.HallID);
            });
            pathEl.addEventListener('mouseover', (e) => {
              pathEl.setAttribute('fill-opacity', '0.52');
              if (tooltipRef.current && tooltipContentRef.current && tooltipDotRef.current) {
                const rect = pathEl.getBoundingClientRect();
                tooltipContentRef.current.innerText = `${hall.HallName} - Status: ${hStat} (${hallBooths.length} booths)`;
                tooltipDotRef.current.style.display = 'inline-block';
                tooltipDotRef.current.style.backgroundColor = color;
                tooltipRef.current.style.left = `${rect.left + rect.width / 2}px`;
                tooltipRef.current.style.top = `${rect.top - 10}px`;
                tooltipRef.current.style.display = 'block';
              }
            });
            pathEl.addEventListener('mouseout', () => {
              pathEl.setAttribute('fill-opacity', '0.35');
              if (tooltipRef.current) tooltipRef.current.style.display = 'none';
            });
            hallLayer.appendChild(pathEl);
          }

          // Floating Card at Hall center
          let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
          hallBooths.forEach((b) => {
            const box = getBoothBox(b.BoothName);
            if (!box) return;
            minX = Math.min(minX, box.x);
            maxX = Math.max(maxX, box.x + box.width);
            minY = Math.min(minY, box.y);
            maxY = Math.max(maxY, box.y + box.height);
          });

          if (minX !== Infinity && maxX > minX) {
            const cx = (minX + maxX) / 2;
            const cy = (minY + maxY) / 2;
            const badgeGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
            badgeGroup.setAttribute('class', 'hall-badge');
            badgeGroup.setAttribute('pointer-events', 'none');

            const daily = hallDailyRoutineSummary.get(hKey);
            const dailyText = (isDailyRoutineActive && daily && daily.total > 0)
              ? ` · ${daily.completed}/${daily.total} Routines`
              : "";
            const subtext = `${hallBooths.length} Stalls${dailyText}`;

            const badgeW = 120;
            const badgeH = 34;

            const bg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
            bg.setAttribute('x', cx - badgeW / 2);
            bg.setAttribute('y', cy - badgeH / 2);
            bg.setAttribute('width', badgeW);
            bg.setAttribute('height', badgeH);
            bg.setAttribute('rx', '5');
            bg.setAttribute('fill', '#ffffff');
            bg.setAttribute('fill-opacity', '0.95');
            bg.setAttribute('stroke', color);
            bg.setAttribute('stroke-width', '2');
            badgeGroup.appendChild(bg);

            const title = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            title.setAttribute('x', cx);
            title.setAttribute('y', cy - 2);
            title.setAttribute('text-anchor', 'middle');
            title.setAttribute('font-size', '11');
            title.setAttribute('font-weight', 'bold');
            title.setAttribute('fill', '#111827');
            title.textContent = `${hall.HallName} (${hStat})`;
            badgeGroup.appendChild(title);

            const subtitle = document.createElementNS('http://www.w3.org/2000/svg', 'text');
            subtitle.setAttribute('x', cx);
            subtitle.setAttribute('y', cy + 11);
            subtitle.setAttribute('text-anchor', 'middle');
            subtitle.setAttribute('font-size', '8.5');
            subtitle.setAttribute('font-weight', '500');
            subtitle.setAttribute('fill', '#6b7280');
            subtitle.textContent = subtext;
            badgeGroup.appendChild(subtitle);

            hallLayer.appendChild(badgeGroup);
          }
        }
      });
    }
  }, [
    currentViewFilter,
    currentDataFilters,
    boothDisplayStatus,
    dotsVersion,
    zoneStatusMap,
    hallStatusMap,
    hallDailyRoutineSummary,
    isDailyRoutineActive,
    filterHallId,
    selectedZoneId,
    booths,
    halls,
    zones,
    zoneStalls,
    effectiveServicePartnerRows,
    effectiveExhibitorOrderRows,
    getShapePath,
    getBoothBox,
    onZoneChange,
    onFilterHallChange
  ]);

  // ─── Focus + spotlight for the active Hall / Booth selection ──────────
  // One effect owns both the framing and the highlight so they can never
  // disagree about what is selected.
  useEffect(() => {
    if (!svgRef.current) return;
    if (booths.length === 0) return;

    if (!filterHallId && !filterBoothId) {
      drawFocusOverlay(null);
      setFocusError("");
      setIsProcessingFilter(false);
      return;
    }

    let finishTimer = null;
    const timer = setTimeout(() => {
      // One box per booth: the spotlight lights exactly the selected stalls and
      // nothing that merely sits between them.
      let boxes = [];
      let label = "";
      let missing = "";
      let picked = [];   // the booths this selection covers, for the traced shape

      const hallBooths = booths.filter(b => b.HallID === filterHallId);

      if (filterBoothId) {
        const booth = boothMap[filterBoothId];
        label = booth ? booth.BoothName : String(filterBoothId);
        picked = [label];
        boxes = [getBoothBox(label)].filter(Boolean);
        if (boxes.length === 0) missing = `Booth "${label}"`;
      } else if (zoneBoothKeys && zoneBoothKeys.size > 0) {
        // A zone has no outline of its own - it groups stalls, it is not a
        // region on the plan. So it is framed and spotlit exactly as a hall is,
        // just over the stalls that belong to it: one mask hole each, which
        // leaves every stall between them dimmed, and the view targeted on
        // their combined extent.
        const zone = (zones || []).find(
          z => String(z.HallZoneMappingID) === String(selectedZoneId)
        );
        label = (zone && zone.Zone) || "Zone";

        // Prefer the stalls in the chosen hall, but do not require it: the zone
        // API and the drawing do not always agree on which hall a stall is in,
        // and dropping the hall test is better than framing nothing.
        let inZone = hallBooths.filter(b =>
          zoneBoothKeys.has(normalizeBoothKey(b.BoothName))
        );
        if (inZone.length === 0) {
          inZone = booths.filter(b => zoneBoothKeys.has(normalizeBoothKey(b.BoothName)));
        }
        picked = inZone.map(b => b.BoothName);
        boxes = inZone.map(b => getBoothBox(b.BoothName)).filter(Boolean);

        // Never leave the map unframed. A zone whose stalls are not in the
        // drawing falls back to the hall, and says so.
        if (boxes.length === 0) {
          missing = `None of zone "${label}"'s ${zoneBoothKeys.size} stalls`;
          picked = hallBooths.map(b => b.BoothName);
          boxes = hallBooths.map(b => getBoothBox(b.BoothName)).filter(Boolean);
        }
      } else if (zoneBoothKeys) {
        // Zone picked, but neither the zone API nor the tracker places a single
        // stall in it. Stay framed on the hall rather than blanking the map.
        const zone = (zones || []).find(
          z => String(z.HallZoneMappingID) === String(selectedZoneId)
        );
        const hall = halls.find(h => h.HallID === filterHallId);
        label = hall ? hall.HallName : String(filterHallId);
        missing = `Zone "${(zone && zone.Zone) || selectedZoneId}" has no stalls mapped to it`;
        picked = hallBooths.map(b => b.BoothName);
        boxes = hallBooths.map(b => getBoothBox(b.BoothName)).filter(Boolean);
      } else {
        const hall = halls.find(h => h.HallID === filterHallId);
        label = hall ? hall.HallName : String(filterHallId);
        picked = hallBooths.map(b => b.BoothName);
        boxes = hallBooths.map(b => getBoothBox(b.BoothName)).filter(Boolean);
        if (boxes.length === 0) missing = `Hall "${label}"`;
      }

      // The traced shapes when we have them - one region for the whole
      // selection - falling back to the boxes for anything not traced.
      const clubbed = Boolean(zoneBoothKeys) && !filterBoothId;
      const shapePath = picked.length > 0 ? getShapePath(picked, clubbed) : null;
      drawFocusOverlay(boxes, boxes.length > 0 ? label : "", shapePath);
      const union = unionBoxes(boxes);
      const zoomed = union ? zoomToSvgBox(union) : false;

      // Failing silently here is what made this look broken - say so instead.
      setFocusError(
        !zoomed
          ? `${missing || label} could not be located in the layout drawing, so the view was not moved.`
          : missing
            ? `${missing} could be found in the drawing - showing the hall instead.`
            : ""
      );

      finishTimer = setTimeout(() => setIsProcessingFilter(false), 450);
    }, 120);

    return () => {
      clearTimeout(timer);
      if (finishTimer) clearTimeout(finishTimer);
    };
  }, [filterHallId, filterBoothId, zoneBoothKeys, selectedZoneId, zones, booths, halls, boothMap, getBoothBox, getShapePath, drawFocusOverlay, zoomToSvgBox]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const touchState = {
      isPinching: false,
      isPanning: false,
      startDistance: 0,
      startScale: 1,
      startMidpoint: { x: 0, y: 0 },
      startOffset: { x: 0, y: 0 },
      lastPan: { x: 0, y: 0 },
    };

    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.hypot(dx, dy);
    };

    const getMidpoint = (touches, rect) => ({
      x: (touches[0].clientX + touches[1].clientX) / 2 - rect.left,
      y: (touches[0].clientY + touches[1].clientY) / 2 - rect.top,
    });

    // A touch that starts inside the popup belongs to the popup.
    const fromPopup = (e) => {
      const t = e.target;
      return !!(t && t.closest && t.closest('[data-booth-popup]'));
    };

    const onTouchStart = (e) => {
      if (fromPopup(e)) return;
      const rect = container.getBoundingClientRect();
      if (e.touches.length === 2) {
        e.preventDefault();
        touchState.isPinching = true;
        touchState.isPanning = false;
        touchState.startDistance = getDistance(e.touches) || 1;
        touchState.startScale = transform.current.scale;
        touchState.startMidpoint = getMidpoint(e.touches, rect);
        touchState.startOffset = { x: transform.current.offsetX, y: transform.current.offsetY };
      } else if (e.touches.length === 1) {
        touchState.isPanning = true;
        touchState.isPinching = false;
        transform.current.hasDragged = false;
        touchState.lastPan = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    const onTouchMove = (e) => {
      if (fromPopup(e)) return;
      if (touchState.isPinching && e.touches.length === 2) {
        e.preventDefault();
        hasUserAdjustedRef.current = true;
        const rect = container.getBoundingClientRect();
        const newDistance = getDistance(e.touches) || 1;
        const scaleFactor = newDistance / touchState.startDistance;
        const newScale = Math.max(0.4, Math.min(8, touchState.startScale * scaleFactor));

        const worldX = (touchState.startMidpoint.x - touchState.startOffset.x) / touchState.startScale;
        const worldY = (touchState.startMidpoint.y - touchState.startOffset.y) / touchState.startScale;

        transform.current.scale = newScale;
        transform.current.offsetX = touchState.startMidpoint.x - worldX * newScale;
        transform.current.offsetY = touchState.startMidpoint.y - worldY * newScale;
        updateTransformStyle();
      } else if (touchState.isPanning && e.touches.length === 1) {
        e.preventDefault();
        const dx = e.touches[0].clientX - touchState.lastPan.x;
        const dy = e.touches[0].clientY - touchState.lastPan.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
          transform.current.hasDragged = true;
          hasUserAdjustedRef.current = true;
        }
        transform.current.offsetX += dx;
        transform.current.offsetY += dy;
        touchState.lastPan = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        updateTransformStyle();
      }
    };

    const onTouchEnd = (e) => {
      if (e.touches.length === 0) {
        touchState.isPinching = false;
        touchState.isPanning = false;
      } else if (e.touches.length === 1) {
        touchState.isPinching = false;
        touchState.isPanning = true;
        touchState.lastPan = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };

    container.addEventListener('touchstart', onTouchStart, { passive: false });
    container.addEventListener('touchmove', onTouchMove, { passive: false });
    container.addEventListener('touchend', onTouchEnd, { passive: false });
    container.addEventListener('touchcancel', onTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [updateTransformStyle]);

  // ─── Route Path Rendering ───────────────────────────────────────────
  useEffect(() => {
    if (!svgRef.current) return;
    const svgElement = svgRef.current.querySelector('svg');
    if (!svgElement) return;
    const routeGroup = svgElement.querySelector('g#route-path');
    if (!routeGroup) return;

    routeGroup.innerHTML = '';

    if (!routeFromBoothId || !routeToBoothId) {
      setRouteError("");
      return;
    }

    if (routeFromBoothId === routeToBoothId) {
      setRouteError("");
      return;
    }

    setIsProcessingFilter(true);
    setFilterMessage("Routing optimal path...");
    setRouteError("");

    const timer = setTimeout(() => {
      const startId = String(routeFromBoothId);
      const endId = String(routeToBoothId);

      const startNode = boothNodesRef.current[startId];
      const endNode = boothNodesRef.current[endId];
      const grid = navGridRef.current;

      if (!startNode || !endNode || !grid) {
        setRouteError(
          !endNode
            ? `Destination "${endId}" does not exist on this layout.`
            : !startNode
              ? `Origin "${startId}" does not exist on this layout.`
              : "The walkable area could not be derived from this layout."
        );
        setIsProcessingFilter(false);
        return;
      }

      // Each stall is a sealed pocket, so step out to the nearest cell that is
      // actually on the aisle network - that is the stall's opening.
      const startCell = nearestAisleCell(grid, startNode.x, startNode.y);
      const endCell = nearestAisleCell(grid, endNode.x, endNode.y);

      if (!startCell || !endCell) {
        setRouteError(`No aisle could be found next to "${!startCell ? startId : endId}".`);
        setIsProcessingFilter(false);
        return;
      }

      const cells = dijkstraOnGrid(grid, startCell, endCell);
      if (!cells || cells.length === 0) {
        setRouteError(`No walkable route connects "${startId}" and "${endId}".`);
        setIsProcessingFilter(false);
        return;
      }

      // Anchor the drawn line on the booths themselves, not the aisle mouths.
      const waypoints = simplifyCells(cells).map(c => cellToPoint(grid, c));
      const points = [
        `${startNode.x},${startNode.y}`,
        ...waypoints.map(p => `${p.x},${p.y}`),
        `${endNode.x},${endNode.y}`,
      ].join(' ');

      const polyline = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
      polyline.setAttribute('points', points);
      polyline.setAttribute('fill', 'none');
      polyline.setAttribute('stroke', '#ef4444'); // red route
      polyline.setAttribute('stroke-width', '2');
      polyline.setAttribute('stroke-dasharray', '6, 5');
      polyline.setAttribute('stroke-linejoin', 'round');
      polyline.setAttribute('stroke-linecap', 'round');
      polyline.setAttribute('pointer-events', 'none');

      const animate = document.createElementNS('http://www.w3.org/2000/svg', 'style');
      animate.textContent = `
        @keyframes dash {
          to { stroke-dashoffset: -16; }
        }
        #route-path polyline {
          animation: dash 1s linear infinite;
        }
        @keyframes routePulse {
          0% { r: 5; opacity: 1; }
          50% { r: 9; opacity: 0.35; }
          100% { r: 5; opacity: 1; }
        }
        #route-path .route-pulse {
          animation: routePulse 1.4s ease-in-out infinite;
          pointer-events: none;
        }
      `;
      routeGroup.appendChild(animate);
      routeGroup.appendChild(polyline);

      // ─── "You are here" marker at the start node ───
      const startPulse = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      startPulse.setAttribute('cx', startNode.x);
      startPulse.setAttribute('cy', startNode.y);
      startPulse.setAttribute('r', '5');
      startPulse.setAttribute('fill', '#22c55e');
      startPulse.setAttribute('fill-opacity', '0.35');
      startPulse.setAttribute('class', 'route-pulse');
      routeGroup.appendChild(startPulse);

      const startDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      startDot.setAttribute('cx', startNode.x);
      startDot.setAttribute('cy', startNode.y);
      startDot.setAttribute('r', '4');
      startDot.setAttribute('fill', '#22c55e');
      startDot.setAttribute('stroke', '#fff');
      startDot.setAttribute('stroke-width', '1.5');
      startDot.setAttribute('pointer-events', 'none');
      routeGroup.appendChild(startDot);

      const startLabelBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      const startLabelText = "You are here";
      startLabelBg.setAttribute('x', startNode.x - 30);
      startLabelBg.setAttribute('y', startNode.y - 22);
      startLabelBg.setAttribute('width', '60');
      startLabelBg.setAttribute('height', '14');
      startLabelBg.setAttribute('rx', '3');
      startLabelBg.setAttribute('fill', '#22c55e');
      startLabelBg.setAttribute('pointer-events', 'none');
      routeGroup.appendChild(startLabelBg);

      const startLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      startLabel.setAttribute('x', startNode.x);
      startLabel.setAttribute('y', startNode.y - 12);
      startLabel.setAttribute('text-anchor', 'middle');
      startLabel.setAttribute('font-size', '7');
      startLabel.setAttribute('fill', '#fff');
      startLabel.setAttribute('font-weight', 'bold');
      startLabel.setAttribute('pointer-events', 'none');
      startLabel.textContent = startLabelText;
      routeGroup.appendChild(startLabel);

      // ─── "Destination" marker at the end node ───
      const endPulse = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      endPulse.setAttribute('cx', endNode.x);
      endPulse.setAttribute('cy', endNode.y);
      endPulse.setAttribute('r', '5');
      endPulse.setAttribute('fill', '#ef4444');
      endPulse.setAttribute('fill-opacity', '0.35');
      endPulse.setAttribute('class', 'route-pulse');
      routeGroup.appendChild(endPulse);

      const endDot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      endDot.setAttribute('cx', endNode.x);
      endDot.setAttribute('cy', endNode.y);
      endDot.setAttribute('r', '4');
      endDot.setAttribute('fill', '#ef4444');
      endDot.setAttribute('stroke', '#fff');
      endDot.setAttribute('stroke-width', '1.5');
      endDot.setAttribute('pointer-events', 'none');
      routeGroup.appendChild(endDot);

      const endLabelBg = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      const endLabelText = "Destination";
      endLabelBg.setAttribute('x', endNode.x - 27);
      endLabelBg.setAttribute('y', endNode.y - 22);
      endLabelBg.setAttribute('width', '54');
      endLabelBg.setAttribute('height', '14');
      endLabelBg.setAttribute('rx', '3');
      endLabelBg.setAttribute('fill', '#ef4444');
      endLabelBg.setAttribute('pointer-events', 'none');
      routeGroup.appendChild(endLabelBg);

      const endLabel = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      endLabel.setAttribute('x', endNode.x);
      endLabel.setAttribute('y', endNode.y - 12);
      endLabel.setAttribute('text-anchor', 'middle');
      endLabel.setAttribute('font-size', '7');
      endLabel.setAttribute('fill', '#fff');
      endLabel.setAttribute('font-weight', 'bold');
      endLabel.setAttribute('pointer-events', 'none');
      endLabel.textContent = endLabelText;
      routeGroup.appendChild(endLabel);

      // Auto-zoom to the whole corridor the route occupies.
      const routePts = [startNode, ...waypoints, endNode];
      const rx = routePts.map(p => p.x);
      const ry = routePts.map(p => p.y);
      const margin = 12;
      zoomToSvgBox({
        x: Math.min(...rx) - margin,
        y: Math.min(...ry) - margin,
        width: (Math.max(...rx) - Math.min(...rx)) + margin * 2,
        height: (Math.max(...ry) - Math.min(...ry)) + margin * 2,
      });
      setIsProcessingFilter(false);
    }, 50); // Small delay to allow the loading UI to render

    return () => clearTimeout(timer);
  }, [routeFromBoothId, routeToBoothId, booths, zoomToSvgBox]);


  if (!layout || !layout.svgContent) {
    return (
      <div className="alert alert-warning" role="alert">
        <i className="ri-alert-line me-2"></i>
        No SVG content available for this layout.
      </div>
    );
  }

  // Hall -> Zone -> Booth. With a zone chosen, only its stalls are offered.
  const hallBooths = filterHallId ? booths.filter(b => b.HallID === filterHallId) : [];
  const filteredBooths = zoneBoothKeys
    ? hallBooths.filter(b => zoneBoothKeys.has(normalizeBoothKey(b.BoothName)))
    : hallBooths;

  const zoneStallCount = zoneBoothKeys ? zoneBoothKeys.size : 0;
  // Zone is a convenience, not a step: "All Zones" offers the whole hall.
  // Booth selection is disabled when not in Stall View or when no hall is selected.
  const boothSelectDisabled = currentViewFilter !== "stall" || !filterHallId;
  const zoneSelectDisabled = currentViewFilter === "hall" || !filterHallId || isZoneLoading || zones.length === 0;

  // Everything the popup can truthfully say about a booth.
  const boothDetail = (() => {
    const empty = { zone: null, tasks: [], exhibitors: [], boothExhibitors: [], vendors: [], orders: [], dailyRoutines: [], exhibitorByService: new Map() };
    if (!selectedBooth) return empty;

    const key = normalizeBoothKey(selectedBooth.BoothName);

    const trackerZoneRow = effectiveServicePartnerRows.find(
      (r) => r.StallName && normalizeBoothKey(r.StallName) === key && (firstValue(r.ZoneName) || firstValue(r.Zone))
    ) || effectiveExhibitorOrderRows.find(
      (r) => (r.Stall || r.StallName) && normalizeBoothKey(r.Stall || r.StallName) === key && (r.Zone || r.ZoneName)
    );

    const zoneRow = (zoneStalls || []).find((s) => normalizeBoothKey(s.StallName) === key);
    const zoneName =
      (trackerZoneRow && (firstValue(trackerZoneRow.ZoneName) || firstValue(trackerZoneRow.Zone) || trackerZoneRow.Zone)) ||
      (zoneRow && zoneRow.Zone) ||
      null;

    const hallKey = String(selectedBooth.HallName || "").trim().toLowerCase();
    if (!hallKey) return { ...empty, zone: zoneName };

    const inHall = (r) => String(r.HallName || r.Hall || "").trim().toLowerCase() === hallKey;
    const isThisBooth = (r) =>
      (r.StallName && normalizeBoothKey(r.StallName) === key) ||
      (r.Stall && normalizeBoothKey(r.Stall) === key);

    const boothExhibitorRow = effectiveServicePartnerRows.find(isThisBooth) || effectiveExhibitorOrderRows.find(isThisBooth);

    // Exhibitors list
    const byExhibitor = new Map();
    const byService = new Map();
    const boothExhibitorIds = new Set();

    effectiveServicePartnerRows.filter(inHall).forEach((r) => {
      if (isThisBooth(r) && r.ExhRegistrationID != null) {
        boothExhibitorIds.add(r.ExhRegistrationID);
      }
      const id = r.ExhRegistrationID != null ? r.ExhRegistrationID : r.ExhRegistrationNo;
      if (id == null) return;
      if (!byExhibitor.has(id)) {
        byExhibitor.set(id, {
          id,
          company: exhibitorField(r, "ExhibitorCompanyName", "CompanyName"),
          regNo: r.ExhRegistrationNo,
          email: exhibitorField(r, "ExhibitorEmail", "Email"),
          mobile: exhibitorField(r, "ExhibitorMobileNo", "MobileNo"),
          stallName: r.StallName || null,
          zone: firstValue(r.ZoneName) || firstValue(r.Zone) || null,
          stallType: r.StallTypeName,
          stallArea: r.StallArea,
          services: new Set(),
        });
      }
      const rowServiceId = firstValue(r.ServiceID);
      const svcName =
        r.name ||
        (rowServiceId != null ? serviceNameById.get(String(rowServiceId)) : null);
      if (svcName) {
        byExhibitor.get(id).services.add(svcName);
        if (!byService.has(svcName)) {
          byService.set(svcName, { id, company: byExhibitor.get(id).company });
        }
      }
    });

    effectiveExhibitorOrderRows.filter(inHall).forEach((r) => {
      if (isThisBooth(r) && r.ExhRegistrationID != null) {
        boothExhibitorIds.add(r.ExhRegistrationID);
      }
      const id = r.ExhRegistrationID != null ? r.ExhRegistrationID : r.ExhRegistrationNo;
      if (id == null) return;
      if (!byExhibitor.has(id)) {
        byExhibitor.set(id, {
          id,
          company: r.Exhibitor || r.ExhibitorCompanyName,
          regNo: r.ExhRegistrationNo,
          email: r.VendorEmail || r.ExhibitorEmail,
          mobile: r.VendorContactNo || r.ExhibitorMobileNo,
          stallName: r.Stall || r.StallName || null,
          zone: r.Zone || r.ZoneName || null,
          stallType: r.StallTypeName,
          stallArea: r.StallArea,
          services: new Set(),
        });
      }
      const svcName = r.Service || (r.ServiceID != null ? serviceNameById.get(String(r.ServiceID)) : null);
      if (svcName) {
        byExhibitor.get(id).services.add(svcName);
        if (!byService.has(svcName)) {
          byService.set(svcName, { id, company: byExhibitor.get(id).company });
        }
      }
    });

    // Service partners on THIS stall (from servicePartnerRows)
    const vendors = Array.from(
      effectiveServicePartnerRows.filter(isThisBooth).reduce((acc, r) => {
        const id =
          r.VendorID != null
            ? r.VendorID
            : vendorField(r, "VendorCompanyName", "CompanyName");
        if (id == null) return acc;
        if (!acc.has(id)) {
          acc.set(id, {
            id,
            company: vendorField(r, "VendorCompanyName", "CompanyName"),
            person: r.vendorPersonName || null,
            mobile: vendorField(r, "VendorMobileNo", "MobileNo"),
            email: r.VendorPrimaryEmail || r.PrimaryEmail || null,
            services: new Set(),
            overdue: 0,
          });
        }
        const svc = serviceNameOf(r);
        if (svc) acc.get(id).services.add(svc);
        if (r.Overdue) acc.get(id).overdue += 1;
        return acc;
      }, new Map()).values()
    );

    // Exhibitor Orders on THIS stall (from exhibitorOrderRows)
    const orders = effectiveExhibitorOrderRows.filter(isThisBooth).map((r) => {
      const isCompleted = (r.IsVerifyByHallManager === 1 || r.IsVerifyByHallManager === true) &&
                          (r.IsVerifyByOrganizer === 1 || r.IsVerifyByOrganizer === true);
      const isPartial = r.IsVerifyByHallManager === 1 || r.IsVerifyByOrganizer === 1;
      const status = isCompleted ? "Completed" : isPartial ? "InProgress" : "Pending";

      return {
        id: r.HMWorkupdationID || `${r.ServiceID}_${r.ExhRegistrationID}`,
        serviceName: r.Service || serviceNameOf(r) || "Service",
        serviceId: r.ServiceID,
        vendorName: r.VendorName || "--",
        contactPerson: r.VendorContactPerson,
        contactNo: r.VendorContactNo,
        status: status,
        possessionStatus: r.Status || "NA",
        lastUpdatedBy: r.LastUpdatedBy,
        remark: r.Remark || r.LastUpdatedRemark,
        isVerifyByHallManager: r.IsVerifyByHallManager,
        isVerifyByOrganizer: r.IsVerifyByOrganizer,
        scheduledDate: r.ScheduledDate,
        exhRegistrationID: r.ExhRegistrationID,
        exhibitorName: r.Exhibitor,
      };
    });

    // Daily routine checklist for this hall (from dailyRoutineRows)
    const dailyRoutines = effectiveDailyRoutineRows.filter((r) =>
      String(r.HallName || r.Hall || "").trim().toLowerCase() === hallKey
    );

    return {
      zone:
        zoneName ||
        (boothExhibitorRow
          ? firstValue(boothExhibitorRow.ZoneName) || firstValue(boothExhibitorRow.Zone)
          : null),
      tasks: (taskRows || []).filter(inHall),
      boothExhibitors: Array.from(byExhibitor.values()).filter((e) =>
        boothExhibitorIds.has(e.id)
      ),
      exhibitors: Array.from(byExhibitor.values()),
      exhibitorByService: byService,
      vendors,
      orders,
      dailyRoutines,
    };
  })();

  const boothExhibitor = boothDetail.boothExhibitors[0] || null;

  const selectedServiceName = selectedServiceId
    ? (() => {
      const s = (services || []).find((x) => String(x.ServiceID) === String(selectedServiceId));
      return s ? s.Name || s.DisplayName || "" : "";
    })()
    : "";

  const popupStatus = boothDisplayStatus(selectedBooth);

  const legendItems = STATUS_LEGEND.concat(
    Object.keys(dynamicStatusCounts)
      .filter((s) => !STATUS_LEGEND.some((i) => i.status === s))
      .map((s) => ({ status: s, color: getStatusColor(s) }))
  );

  const handleWheel = (e) => {
    e.preventDefault();
    if (!containerRef.current) return;
    hasUserAdjustedRef.current = true;
    const rect = containerRef.current.getBoundingClientRect();
    const pointX = e.clientX - rect.left;
    const pointY = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    zoomAtPoint(transform.current.scale * delta, pointX, pointY);
    updateTransformStyle();
  };

  const handleMouseDown = (e) => {
    if (e.button === 0) {
      transform.current.isDragging = true;
      transform.current.hasDragged = false;
      transform.current.dragStart = { x: e.clientX, y: e.clientY };
      transform.current.dragInitial = { x: e.clientX, y: e.clientY };
      if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e) => {
    if (transform.current.isDragging) {
      const dxTotal = e.clientX - transform.current.dragInitial.x;
      const dyTotal = e.clientY - transform.current.dragInitial.y;
      if (Math.abs(dxTotal) > 5 || Math.abs(dyTotal) > 5) {
        transform.current.hasDragged = true;
        hasUserAdjustedRef.current = true;
      }

      const dx = e.clientX - transform.current.dragStart.x;
      const dy = e.clientY - transform.current.dragStart.y;
      transform.current.offsetX += dx;
      transform.current.offsetY += dy;
      transform.current.dragStart = { x: e.clientX, y: e.clientY };
      updateTransformStyle();
    }
  };

  const handleMouseUp = () => {
    transform.current.isDragging = false;
    if (containerRef.current) containerRef.current.style.cursor = 'grab';
  };

  const zoomIn = () => {
    if (!containerRef.current) return;
    hasUserAdjustedRef.current = true;
    const rect = containerRef.current.getBoundingClientRect();
    zoomAtPoint(transform.current.scale * 1.2, rect.width / 2, rect.height / 2);
    updateTransformStyle(true);
  };

  const zoomOut = () => {
    if (!containerRef.current) return;
    hasUserAdjustedRef.current = true;
    const rect = containerRef.current.getBoundingClientRect();
    zoomAtPoint(transform.current.scale * 0.8, rect.width / 2, rect.height / 2);
    updateTransformStyle(true);
  };

  const handleReset = () => {
    setIsProcessingFilter(true);
    setFilterMessage("Resetting layout view & filters...");

    hasUserAdjustedRef.current = false;
    if (!applyDefaultView(true)) {
      transform.current.scale = 1;
      transform.current.offsetX = 0;
      transform.current.offsetY = 0;
      updateTransformStyle(true);
    }

    drawFocusOverlay(null);
    if (onFilterHallChange) onFilterHallChange("");
    if (onFilterBoothChange) onFilterBoothChange("");
    if (onZoneChange) onZoneChange("");
    if (onServiceChange) onServiceChange("");
    setRouteFromBoothId("");
    setRouteToBoothId("");
    setRouteError("");
    setFocusError("");

    setTimeout(() => {
      setIsProcessingFilter(false);
    }, 400);
  };

  const handleHallFilterChange = (e) => {
    const hallId = e.target.value;
    setIsProcessingFilter(true);
    setFilterMessage(hallId ? "Filtering & zooming to Hall..." : "Resetting hall filter...");
    if (onFilterHallChange) onFilterHallChange(hallId);
    if (onFilterBoothChange) onFilterBoothChange("");

    if (!hallId) {
      hasUserAdjustedRef.current = false;
      if (!applyDefaultView(true)) {
        transform.current.scale = 1;
        transform.current.offsetX = 0;
        transform.current.offsetY = 0;
        updateTransformStyle(true);
      }

      setTimeout(() => {
        setIsProcessingFilter(false);
      }, 400);
    }
  };

  const handleBoothFilterChange = (e) => {
    const boothId = e.target.value;
    setIsProcessingFilter(true);
    setFilterMessage(boothId ? "Focusing on selected Booth..." : "Viewing all booths in Hall...");
    if (onFilterBoothChange) onFilterBoothChange(boothId);
  };

  return (
    <div className="svg-canvas-container text-start">
      {/* ─── Title & Toolbar Card ─── */}
      <div className="card shadow-none border p-2 mb-2 bg-white" style={{ borderRadius: '2px', borderColor: '#e4e7eb' }}>
        <h6 className="mb-2 mt-0 fw-semibold text-start" style={{ color: '#007062', fontSize: '16px' }}>
          <i className="ri-map-2-line me-2"></i><span>Hall Layout View</span>
        </h6>
        <div className="svg-toolbar d-flex gap-2 flex-wrap align-items-center">
          <div className="btn-group" role="group">
            <button type="button" className="btn btn-sm bg-white border rounded-0 d-flex align-items-center justify-content-center" onClick={zoomIn} title="Zoom In" style={{ color: '#4a4a4a', height: '32px' }}>
              <i className="ri-zoom-in-line"></i>
            </button>
            <button type="button" className="btn btn-sm bg-white border rounded-0 d-flex align-items-center justify-content-center" disabled ref={zoomLabelRef} style={{ color: '#4a4a4a', height: '32px' }}>
              100%
            </button>
            <button type="button" className="btn btn-sm bg-white border rounded-0 d-flex align-items-center justify-content-center" onClick={zoomOut} title="Zoom Out" style={{ color: '#4a4a4a', height: '32px' }}>
              <i className="ri-zoom-out-line"></i>
            </button>
          </div>

          {/* ─── 1. View Filter Dropdown (Main Filter) ──────────────── */}
          <div className="d-flex align-items-center gap-1">
            <select
              className="form-control form-control-sm rounded-0 fw-semibold"
              style={{ minWidth: '135px', maxWidth: '160px', height: '32px', borderColor: '#007062', color: '#007062', backgroundColor: '#f0fdfa' }}
              value={currentViewFilter}
              onChange={(e) => handleViewFilterSelect(e.target.value)}
              title="Select View Mode"
            >
              <option value="stall">1 - Stall View</option>
              <option value="zone">2 - Zone Status</option>
              <option value="hall">3 - Hall Status</option>
            </select>
          </div>

          {/* ─── 2. Data Filter Multi-Select Checkbox Dropdown ───────── */}
          <div className="dropdown position-relative d-inline-block">
            <button
              type="button"
              className="btn btn-sm bg-white border rounded-0 d-flex align-items-center gap-1 px-2"
              style={{ height: '32px', color: '#1f2937', minWidth: '145px', justifyContent: 'space-between' }}
              onClick={() => setIsDataFilterOpen(!isDataFilterOpen)}
              title="Select Data Filters"
            >
              <span className="d-flex align-items-center gap-1 text-truncate">
                <i className="ri-filter-3-line text-primary"></i>
                <span className="fw-medium small">Data ({currentDataFilters.length})</span>
              </span>
              <i className={`ri-arrow-${isDataFilterOpen ? 'up' : 'down'}-s-line text-muted`}></i>
            </button>

            {isDataFilterOpen && (
              <div
                className="dropdown-menu show shadow-lg p-2 position-absolute"
                style={{
                  top: '100%',
                  left: 0,
                  zIndex: 1060,
                  minWidth: '220px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                }}
              >
                <div className="d-flex justify-content-between align-items-center pb-1 mb-2 border-bottom">
                  <span className="small fw-bold text-dark">Data Filter</span>
                  <span className="badge bg-primary-subtle text-primary small">Multi-select</span>
                </div>
                <label className="dropdown-item d-flex align-items-center gap-2 py-1 px-2 rounded mb-1" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    className="form-check-input mt-0"
                    checked={currentDataFilters.includes('service_partner')}
                    onChange={() => handleToggleDataFilter('service_partner')}
                  />
                  <span className="small text-dark">Service Partner Task</span>
                </label>
                <label className="dropdown-item d-flex align-items-center gap-2 py-1 px-2 rounded mb-1" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    className="form-check-input mt-0"
                    checked={currentDataFilters.includes('exhibitor_orders')}
                    onChange={() => handleToggleDataFilter('exhibitor_orders')}
                  />
                  <span className="small text-dark">Exhibitor Orders</span>
                </label>
                <label className="dropdown-item d-flex align-items-center gap-2 py-1 px-2 rounded mb-1" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    className="form-check-input mt-0"
                    checked={currentDataFilters.includes('daily_routine')}
                    onChange={() => handleToggleDataFilter('daily_routine')}
                  />
                  <span className="small text-dark">Daily Routine Task</span>
                </label>
              </div>
            )}
          </div>

          {/* ─── Hall Filter Dropdown ─────────────────────────────────── */}
          <div className="d-flex align-items-center gap-1">
            <select
              className="form-control form-control-sm rounded-0"
              style={{ minWidth: '125px', maxWidth: '155px', height: '32px' }}
              value={filterHallId || ""}
              onChange={handleHallFilterChange}
              title="Select Hall to zoom"
            >
              <option value="">All Halls</option>
              {halls.map((hall) => (
                <option key={hall.HallID} value={hall.HallID}>
                  {hall.HallName} ({hall.BoothCount})
                </option>
              ))}
            </select>
          </div>

          {/* ─── Zone Filter Dropdown (cascades from Hall) ────────────── */}
          <div className="d-flex align-items-center gap-1">
            <select
              className="form-control form-control-sm rounded-0"
              style={{ minWidth: '125px', maxWidth: '155px', height: '32px' }}
              value={selectedZoneId || ""}
              onChange={(e) => onZoneChange && onZoneChange(e.target.value)}
              disabled={zoneSelectDisabled}
              title={
                currentViewFilter === "hall" ? "Zone filter disabled in Hall Status view"
                  : !filterHallId ? "Select a Hall first"
                    : isZoneLoading ? "Loading zones..."
                      : zones.length === 0 ? "No zones for this hall"
                        : "Select Zone"
              }
            >
              <option value="">
                {currentViewFilter === "hall" ? "N/A in Hall View" : isZoneLoading ? "Loading zones..." : zones.length === 0 ? "No Zones" : "All Zones"}
              </option>
              {zones.map((zone) => (
                <option key={zone.HallZoneMappingID} value={zone.HallZoneMappingID}>
                  {zone.Zone}
                </option>
              ))}
            </select>
          </div>

          {/* ─── Booth Filter Dropdown (cascades from Zone) ───────────── */}
          <div className="d-flex align-items-center gap-1">
            <select
              className="form-control form-control-sm rounded-0"
              style={{ minWidth: '125px', maxWidth: '155px', height: '32px' }}
              value={filterBoothId || ""}
              onChange={handleBoothFilterChange}
              disabled={boothSelectDisabled}
              title={
                currentViewFilter !== "stall" ? "Booth filter available in Stall View only"
                  : !filterHallId ? "Select a Hall first"
                    : "Select Booth to zoom"
              }
            >
              <option value="">{currentViewFilter !== "stall" ? "N/A in Zone/Hall" : "All Booths"}</option>
              {filteredBooths.map((booth) => (
                <option key={booth.BoothName} value={booth.BoothName}>
                  {booth.BoothName}
                </option>
              ))}
            </select>
          </div>

          {/* ─── Service Filter ───────────────── */}
          <div className="d-flex align-items-center gap-1">
            <select
              className="form-control form-control-sm rounded-0"
              style={{ minWidth: '125px', maxWidth: '155px', height: '32px' }}
              value={selectedServiceId || ""}
              onChange={(e) => onServiceChange && onServiceChange(e.target.value)}
              disabled={services.length === 0}
              title={services.length === 0 ? "No services available" : "Filter by Service"}
            >
              <option value="">All Services</option>
              {services.map((svc) => (
                <option key={svc.ServiceID} value={svc.ServiceID}>
                  {svc.Name || svc.DisplayName}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="btn btn-sm bg-white d-flex align-items-center justify-content-center flex-shrink-0 rounded-0"
            style={{ border: '1px solid #007062', color: '#007062', height: '32px' }}
            onClick={() => onOpenTaskDetail && onOpenTaskDetail()}
            title="Open the task list for the selected Hall and Service"
          >
            <i className="ri-file-text-line me-1"></i>
            Tasks
          </button>

          <button
            type="button"
            className="btn btn-sm bg-white border rounded-0 d-flex align-items-center justify-content-center"
            style={{ color: '#4a4a4a', height: '32px' }}
            onClick={handleReset}
            title="Reset View & Filters"
          >
            <i className="ri-refresh-line me-1"></i>
            Reset
          </button>

          {isProcessingFilter && (
            <div className="d-flex align-items-center gap-2 text-primary fw-semibold bg-primary-subtle border border-primary-subtle px-2 py-1 rounded">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <small>{filterMessage || "Processing filter..."}</small>
            </div>
          )}
        </div>
      </div>

      {/* ─── Focus (hall / booth) warning banner ─── */}
      {focusError && (
        <div className="alert alert-warning d-flex align-items-center py-2 px-3 mb-3" role="alert">
          <i className="ri-crosshair-line me-2"></i>
          <span className="small fw-semibold">{focusError}</span>
          <button
            type="button"
            className="btn-close ms-auto"
            style={{ fontSize: '0.7rem' }}
            onClick={() => setFocusError("")}
            aria-label="Dismiss"
          ></button>
        </div>
      )}

      {/* ─── Route error banner ─── */}
      {routeError && (
        <div className="alert alert-warning d-flex align-items-center py-2 px-3 mb-3" role="alert">
          <i className="ri-error-warning-line me-2"></i>
          <span className="small fw-semibold">{routeError}</span>
          <button
            type="button"
            className="btn-close ms-auto"
            style={{ fontSize: '0.7rem' }}
            onClick={() => setRouteError("")}
            aria-label="Dismiss"
          ></button>
        </div>
      )}

      {/* ─── Status legend: one compact row, sits directly above the map ─── */}
      <div className="d-flex flex-wrap align-items-center gap-3 mb-2 small ">
        <span className="fw-semibold text-dark">
          <i className="ri-palette-line me-1"></i>
          {currentViewFilter === "stall" ? "Stall Status" : currentViewFilter === "zone" ? "Zone Status" : "Hall Status"}:
        </span>
        {legendItems.map((item) => (
          <span key={item.status} className="d-inline-flex align-items-center gap-1">
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: item.color,
                border: '1px solid #d1d5db',
                display: 'inline-block',
              }}
            />
            {item.status}
            {!isStatusLoading && (dynamicStatusCounts[item.status] || 0) > 0 && (
              <strong className="text-dark">{dynamicStatusCounts[item.status]}</strong>
            )}
          </span>
        ))}
        {isStatusLoading && (
          <span className="d-inline-flex align-items-center gap-1">
            <span className="spinner-border spinner-border-sm" role="status" style={{ width: '0.7rem', height: '0.7rem' }}></span>
          </span>
        )}
      </div>

      {/* SVG Canvas and Sidebar Container */}
      <div className="position-relative border border-secondary-subtle rounded overflow-hidden bg-white p-0">
        {/* SVG Canvas Area */}
        <div ref={canvasWrapRef} style={{ width: "100%" }}>
          <div
            ref={containerRef}
            className="canvas-container position-relative"
            style={{
              width: "100%",
              height: `${canvasBox.height}px`,
              overflow: "hidden",
              cursor: "grab",
              backgroundColor: "#f8f9fa",
              userSelect: "none",
              position: "relative",
              touchAction: "none",
            }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {isProcessingFilter && (
              <div
                className="position-absolute d-flex align-items-center gap-2 bg-white shadow-sm border rounded-pill px-3 py-2"
                style={{
                  top: "12px",
                  left: "12px",
                  zIndex: 10,
                  pointerEvents: "none",
                }}
              >
                <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: "1rem", height: "1rem" }}>
                  <span className="visually-hidden">Processing...</span>
                </div>
                <small className="fw-semibold text-dark">{filterMessage || "Processing filter..."}</small>
              </div>
            )}

            <MemoizedSvg ref={svgRef} svgContent={layout.svgContent} />

          </div>
        </div>

        {/* Sidebar Area for Selected Booth Details (Overlapping) */}
        {selectedBooth && (
          <div
            className="position-absolute shadow-lg bg-white d-flex flex-column border-end"
            style={{
              top: 0,
              left: 0,
              width: '390px',
              height: '100%',
              zIndex: 20
            }}
            onWheel={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center p-2 border-bottom sticky-top" style={{ backgroundColor: '#f3f4f6' }}>
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0 fw-semibold d-flex align-items-center text-dark" style={{ fontSize: '14px' }}>
                  {selectedBooth.BoothName}
                </h6>
                <div className="d-flex align-items-center gap-1 rounded-pill px-2 py-1" style={{ backgroundColor: '#e2e8f0', fontSize: '11px', fontWeight: '500' }}>
                  <i className="ri-map-pin-line"></i>
                  <span>Hall {selectedBooth.HallName || selectedBooth.HallID || '--'}</span>
                  <span className="mx-1">·</span>
                  <span>Zone {boothDetail.zone || '--'}</span>
                </div>
              </div>
              <button
                type="button" style={{ fontSize: '10px' }}
                className="btn-close"
                aria-label="Close"
                onClick={() => setSelectedBooth(null)}
              ></button>
            </div>
            <div className="p-2 flex-grow-1 overflow-auto pb-3 pt-1">

              <div className="d-flex align-items-center gap-2 mb-2 p-1 bg-light rounded border">
                <span style={{ fontSize: '11px', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  Status:
                </span>
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: popupStatus.dimmed ? DIMMED_DOT_COLOR : getStatusColor(popupStatus.status)
                  }}
                  className="flex-shrink-0"
                />
                <span className={popupStatus.dimmed ? 'text-muted' : 'fw-semibold text-dark'} style={{ fontSize: '13px' }}>
                  {popupStatus.dimmed
                    ? (boothDetail.vendors.length > 0 || boothDetail.orders.length > 0 ? 'Unallocated' : 'No tasks recorded')
                    : popupStatus.status || 'Unknown'}
                </span>
              </div>

              {/* Exhibitor Details Card */}
              {boothDetail.boothExhibitors.length > 0 && (
                <div className="card shadow-none border mb-2 p-1 pt-0" style={{ borderRadius: '4px' }}>
                  <div className="card-header bg-white p-1 border-bottom">
                    <span className="fw-bold" style={{ color: '#007062', fontSize: '11px', letterSpacing: '0.5px' }}>EXHIBITOR DETAILS</span>
                  </div>
                  <div className="card-body p-1">
                    {boothDetail.boothExhibitors.map((ex) => (
                      <div key={ex.id}>
                        <h6 className="fw-semibold text-dark mb-0 mt-1" style={{ fontSize: '13px' }}>{ex.company || "--"}</h6>
                        {ex.regNo && (
                          <div className="small text-muted mb-1"># {ex.regNo}</div>
                        )}
                        {(ex.stallType || ex.stallArea != null) && (
                          <div className="d-inline-flex align-items-center bg-light border rounded px-2 py-1 mb-2">
                            <i className="ri-layout-grid-line me-2 text-secondary"></i>
                            <span className="small">{ex.stallType || "Stall"}{ex.stallArea != null ? ` — ${ex.stallArea} sqm` : ""}</span>
                          </div>
                        )}

                        {(ex.mobile || ex.email) && (
                          <div className="row g-0 rounded p-0">
                            {ex.mobile && (
                              <div className="col-6">
                                <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>
                                  <i className="ri-phone-line me-1"></i> Phone
                                </div>
                                <div className="small text-dark mt-0">{ex.mobile}</div>
                              </div>
                            )}
                            {ex.email && (
                              <div className="col-6">
                                <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#6b7280' }}>
                                  <i className="ri-mail-line me-1"></i> Email
                                </div>
                                <div className="small text-dark mt-0 text-break">{ex.email}</div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* SECTION 1: Service Partner Tasks (if isServicePartnerActive) */}
              {isServicePartnerActive && (
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-1 mt-1">
                    <span className="fw-semibold text-dark" style={{ fontSize: '12.5px' }}>
                      <i className="ri-truck-line me-1 text-primary"></i>Service Partner Tasks
                    </span>
                    {boothDetail.vendors.length > 0 && (
                      <span className="badge rounded-pill bg-primary" style={{ fontSize: '10px' }}>
                        {boothDetail.vendors.length}
                      </span>
                    )}
                  </div>

                  {isTaskLoading ? (
                    <div className="small text-muted p-2">
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>Loading...
                    </div>
                  ) : boothDetail.vendors.length === 0 ? (
                    <div className="small text-muted fst-italic p-2 bg-light border rounded mb-2">
                      No service partners recorded against {selectedBooth.BoothName}.
                    </div>
                  ) : (
                    boothDetail.vendors.map((v) => (
                      <div key={v.id} className="card shadow-sm border mb-2 p-0" style={{ borderRadius: '6px' }}>
                        <div className="p-2">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <h6 className="fw-semibold text-dark mb-0" style={{ fontSize: '12.5px' }}>{v.company || "--"}</h6>
                            {v.overdue > 0 && (
                              <span className="badge flex-shrink-0" style={{ backgroundColor: '#fee2e2', color: '#ef4444', fontWeight: 600, fontSize: '9.5px' }}>
                                <i className="ri-error-warning-fill me-1"></i>{v.overdue} OVERDUE
                              </span>
                            )}
                          </div>

                          {v.services.size > 0 && (
                            <div className="mb-1">
                              {Array.from(v.services).map((svc) => (
                                <span key={svc} className="d-inline-flex align-items-center bg-light border rounded px-2 py-0.5 small me-1 mb-1" style={{ fontSize: '11px' }}>
                                  {svc}
                                  {(() => {
                                    const target = boothExhibitor || boothDetail.exhibitorByService.get(svc) || null;
                                    if (!target) return null;
                                    return (
                                      <span
                                        role="button"
                                        className="ms-1 text-primary"
                                        style={{ cursor: "pointer", fontSize: "13px", lineHeight: 1 }}
                                        title={`Open the ${svc} form for ${target.company || "this exhibitor"}`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          onOpenManualForm && onOpenManualForm(svc, target.id, target.company);
                                        }}
                                      >
                                        <i className="ri-information-line"></i>
                                      </span>
                                    );
                                  })()}
                                </span>
                              ))}
                            </div>
                          )}

                          {(v.person || v.mobile || v.email) && (
                            <div className="small text-muted pt-1 border-top mt-1" style={{ fontSize: '11px' }}>
                              {v.person && <div><i className="ri-user-3-line me-1"></i>{v.person}</div>}
                              {v.mobile && <div><i className="ri-phone-line me-1"></i>{v.mobile}</div>}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* SECTION 2: Exhibitor Orders & Services (if isExhibitorOrdersActive) */}
              {isExhibitorOrdersActive && (
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-1 mt-2">
                    <span className="fw-semibold text-dark" style={{ fontSize: '12.5px' }}>
                      <i className="ri-shopping-cart-line me-1 text-success"></i>Exhibitor Orders & Services
                    </span>
                    {boothDetail.orders.length > 0 && (
                      <span className="badge rounded-pill bg-success" style={{ fontSize: '10px' }}>
                        {boothDetail.orders.length}
                      </span>
                    )}
                  </div>

                  {boothDetail.orders.length === 0 ? (
                    <div className="small text-muted fst-italic p-2 bg-light border rounded mb-2">
                      No exhibitor orders recorded for {selectedBooth.BoothName}.
                    </div>
                  ) : (
                    boothDetail.orders.map((ord) => (
                      <div key={ord.id} className="card shadow-sm border mb-2 p-0" style={{ borderRadius: '6px' }}>
                        <div className="p-2">
                          <div className="d-flex justify-content-between align-items-start mb-1">
                            <span className="fw-semibold text-dark" style={{ fontSize: '12px' }}>
                              {ord.serviceName}
                              <span
                                role="button"
                                className="ms-1 text-primary"
                                style={{ cursor: "pointer", fontSize: "13px" }}
                                title={`Open ${ord.serviceName} Form`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onOpenManualForm && onOpenManualForm(ord.serviceName, ord.exhRegistrationID, ord.exhibitorName);
                                }}
                              >
                                <i className="ri-information-line"></i>
                              </span>
                            </span>
                            <span
                              className="badge"
                              style={{
                                backgroundColor: getStatusColor(ord.status),
                                color: '#ffffff',
                                fontSize: '10px'
                              }}
                            >
                              {ord.status}
                            </span>
                          </div>

                          <div className="small text-muted" style={{ fontSize: '11px' }}>
                            <div><strong>Vendor:</strong> {ord.vendorName}</div>
                            {ord.possessionStatus && ord.possessionStatus !== "NA" && (
                              <div><strong>Possession:</strong> {ord.possessionStatus}</div>
                            )}
                            {ord.remark && <div><strong>Remark:</strong> {ord.remark}</div>}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* SECTION 3: Daily Routine Task Summary (if isDailyRoutineActive) */}
              {isDailyRoutineActive && boothDetail.dailyRoutines.length > 0 && (
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-1 mt-2">
                    <span className="fw-semibold text-dark" style={{ fontSize: '12.5px' }}>
                      <i className="ri-calendar-check-line me-1 text-info"></i>Hall Daily Routines ({boothDetail.dailyRoutines.length})
                    </span>
                  </div>
                  {boothDetail.dailyRoutines.map((dr, idx) => (
                    <div key={idx} className="card shadow-none border mb-1 p-2 bg-light" style={{ borderRadius: '4px', fontSize: '11px' }}>
                      <div className="d-flex justify-content-between">
                        <strong>{dr.ProgramTitle}</strong>
                        <span className="badge bg-secondary">{dr.HallStatus}</span>
                      </div>
                      <div className="text-muted mt-1">
                        <div>Time: {dr.ScheduleTimes}</div>
                        <div>Responsible: {dr.ResponsiblePerson}</div>
                        <div>Completed: {dr.TotalCompCount}/{dr.TotalCount}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        )}
      </div>
      {/* Tooltip (DOM updated directly via ref) */}
      <div
        ref={tooltipRef}
        style={{
          position: "fixed",
          display: "none",
          backgroundColor: "#1f2937",
          color: "white",
          padding: "8px 12px",
          borderRadius: "4px",
          fontSize: "12px",
          pointerEvents: "none",
          zIndex: 1000,
          whiteSpace: "nowrap",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.4)",
          border: "1px solid #374151",
          maxWidth: "250px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          transform: "translateY(-100%)",
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span
            ref={tooltipDotRef}
            style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
            }}
          />
          <span ref={tooltipContentRef}></span>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: "-5px",
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderTop: "5px solid #1f2937",
          }}
        />
      </div>

      {/* Info Section */}
      <div className="mt-3 p-3 bg-light rounded d-flex justify-content-between align-items-center">
        <div>
          <small className=" d-block">
            <strong>Layout Name:</strong> {layout.LayoutName}
          </small>
          <small className=" d-block">
            <strong>Halls in drawing:</strong> {halls.length}
            <span className="mx-2">|</span>
            <strong>Booths in drawing:</strong> {booths.length}
          </small>
        </div>

      </div>

    </div>
  );
};

export default SvgCanvas;